/**
 * v1.5.0 GastroGest — modo hospitalidad (restaurantes). UI demo-presentable sobre
 * el kernel de comercio (mesas + comanda + cobro + propina + IVA). Hidrata desde
 * los endpoints reales /pos/* y /boutique/* (catálogo compartido del kernel); el
 * IVA y los totales son server-side (no se calculan en el cliente).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  UtensilsCrossed, Plus, Trash2, Receipt, CreditCard, Banknote,
  ArrowLeftRight, X, ChevronLeft, Pencil, ChefHat, Printer, Layers, Minus, Users, Sparkles,
  Bell, Check,
} from 'lucide-react';
import { Stepper } from './ModifiersView';
import { api } from '../lib/api';
import {
  kitchenColor, clampKitchenAlertMinutes, fmtElapsed, KITCHEN_DOT, KITCHEN_TEXT,
} from '../lib/kitchen';
import { useAuth } from '../auth/AuthProvider';
import { computeChange, nextRoundAbove } from '../lib/cash';
import QRCode from 'qrcode';
import { kitchenTicket, salesTicket, cancelTicket } from '../lib/escpos';
import { comandaHtml, salesTicketHtml, cancelHtml } from '../printing/ticket';
import { fetchTicketSettings, resolveBranding } from '../printing/config';
import { printTicket, PrinterSettingsModal, getPrintersFor } from './printing';
import { decideKitchenPrint, kitchenSentMessage } from '../printing/kitchenPrint';
// Capa 2 (V96) — factura CFDI on-demand de una venta pagada.
import { FacturarCfdiButton } from '../components/SalesCfdiDialog';
import { pollStillValid, batchJustFired } from './comandaSync';
import { CaptureTracker } from './captureBarrier';
import { useFloorActing } from './FloorLock';
import { SelfInvoiceTicketBlock } from '../components/SelfInvoiceTicketBlock';
// V205 — Fidelidad §29: identificar al miembro, "lo de siempre" y canje en la comanda.
import { LoyaltyPosBox, type OrderLoyalty } from '../loyalty/LoyaltyPosBox';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

function Header({ title, subtitle, action }: { title: string; subtitle: string; action?: any }) {
  return (
    // flex-wrap + gap: en móvil la fila de acción (p.ej. 3 botones en Mesas) baja
    // bajo el título en vez de forzar el ancho del layout; min-w-0 deja truncar.
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="truncate font-serif text-2xl tracking-tight text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

// ===========================================================================
// Tipos mínimos (la API es la fuente de verdad)
// ===========================================================================
interface Table { id: string; name: string; zone: string | null; capacity: number | null; status: 'FREE' | 'OCCUPIED'; occupiedByName?: string | null; occupiedByUserId?: string | null; awaitingPayment?: boolean; assignedUserId?: string | null; assignedUserName?: string | null; partialBalance?: number | null; accountCount?: number; }
interface Variant { id: string; name: string; price: number; available?: number; }
// V86 — "Arma tu platillo": grupos de modificadores del producto (los adjunta
// /boutique/products; el server valida min/max y resuelve precios — la UI solo guía).
interface ModOption { id: string; name: string; priceDelta: number; }
interface ModGroup { id: string; name: string; minSelect: number; maxSelect: number | null; required: boolean; options: ModOption[]; }
interface MenuItem { kind: 'product' | 'service'; id: string; name: string; price: number; category: string; variants?: Variant[]; modifierGroups?: ModGroup[]; isPrixFixe?: boolean; }
interface ItemModifier { id?: string; name: string; price: number; groupName?: string | null; modifierOptionId?: string | null; }
interface OrderItem {
  id: string; description: string; qty: number; unitPrice: number; lineTotal: number;
  productId?: string | null;
  notes?: string | null; modifiers?: ItemModifier[];
  firedAt?: string | null; // V49 — enviado a cocina (null = pendiente)
  readyAt?: string | null;  // V131 (§31.2) — marcado listo en cocina (null = pendiente)
  /** §17 Part A — el renglón se cocina (tiene receta). false = listo-para-servir
   *  (bebida/embotellado): se ENTREGA directo desde la comanda sin pasar por cocina. */
  requiresKitchen?: boolean;
  /** §17 Part A — el producto controla inventario (exige elección íntegro/dañado al
   *  cancelar si ya fue entregado). */
  tracksInventory?: boolean;
}
interface Payment { method: string; amount: number; tip: number; cashReceived?: number | null; roundingDiff?: number; }
interface Order {
  id: string; tableId: string | null; status: string; customerLabel: string | null;
  /** V117 — comensales (null = no capturado; editable mientras OPEN). */
  diners: number | null;
  subtotal: number; taxTotal: number; total: number;
  items: OrderItem[]; payments: Payment[];
  /** Momento de cierre O cancelación (el backend sella closed_at en ambos). */
  closedAt?: string | null;
  /** §32/A0 — pedido para recoger: ciclo de entrega + código. null = mesa/venta normal. */
  fulfillmentStatus?: 'pending_prepare' | 'in_kitchen' | 'ready' | 'delivered' | null;
  deliveryCode?: string | null;
  /** 🅐 pieza 1 — pre-cuenta impresa, esperando cobro (null = no espera). */
  awaitingPaymentSince?: string | null;
  /** V205 — miembro de fidelidad ligado + avance (el ticket sellado imprime la línea). */
  loyalty?: OrderLoyalty | null;
  /** V205 — ticket de promoción $0 (canje de fidelidad). */
  isLoyaltyCourtesy?: boolean;
}

/**
 * PRESENTACIÓN — agrupa líneas IDÉNTICAS para mostrar "N×" en una sola (el backend crea
 * una fila por cada "+"; no las fusiona). Clave = producto + precio + TODOS los
 * modificadores/extras + nota; incluye el estado de cocina (fired) para que el badge
 * "en cocina" quede exacto (2 idénticos, uno enviado y otro no, se ven en líneas aparte).
 * NO toca los datos: fires y "Enviar a cocina" siguen contando por unidad (usan order.items
 * crudo). Los botones actúan sobre una unidad representativa del grupo.
 */
interface ItemGroup { rep: OrderItem; ids: string[]; qty: number; lineTotal: number; }
function orderItemKey(it: OrderItem): string {
  const mods = (it.modifiers ?? [])
    .map((m) => `${m.modifierOptionId ?? ''}~${m.name}~${Number(m.price) || 0}`)
    .sort()
    .join('|');
  return [
    it.productId ?? '', it.description, Number(it.unitPrice) || 0,
    (it.notes ?? '').trim(), mods, it.firedAt ? 'fired' : 'open', it.readyAt ? 'ready' : 'pend',
  ].join('§');
}
function groupOrderItems(items: OrderItem[]): ItemGroup[] {
  const map = new Map<string, ItemGroup>();
  const order: string[] = [];
  for (const it of items) {
    const key = orderItemKey(it);
    let g = map.get(key);
    if (!g) { g = { rep: it, ids: [], qty: 0, lineTotal: 0 }; map.set(key, g); order.push(key); }
    g.ids.push(it.id);
    g.qty += Number(it.qty) || 0;
    g.lineTotal += Number(it.lineTotal) || 0;
  }
  return order.map((k) => map.get(k)!);
}

// ===========================================================================
// Pantalla principal: Mesas → Comanda → Cobro → Ticket
// ===========================================================================
/**
 * El MISMO POS en dos modos (mismo pipeline de cobro/ticket/cierre):
 *  - 'tables' (Gastro): mesas → comanda → cobro (comportamiento de siempre).
 *  - 'quick'  (Boutique/Wellness): sin mesas; abre directo el carrito → cobrar.
 * `sourceModule` etiqueta la venta para que la analítica cuadre por módulo (V46);
 * en modo quick también define el catálogo (boutique: servicios+productos; gym
 * (Wellness): solo productos).
 */
export function GastroGestPOS({
  me, mode = 'tables', sourceModule = 'gastro',
}: {
  me?: any;
  mode?: 'tables' | 'quick';
  sourceModule?: 'gastro' | 'boutique' | 'gym';
}) {
  const quick = mode === 'quick';
  // PINK #1 (P1) — sesión compartida de tablet: se pide PIN al ABRIR una orden
  // (mesa nueva o venta rápida). El PIN identifica, no autoriza; reabrir una
  // comanda existente NO pide PIN (opened_by nunca cambia).
  const isFloor = !!me?.isFloor;
  // 🔴 Corrección #493 (11/09) — REGLA: el acting token del DESBLOQUEO es la
  // ÚNICA identidad de piso. Aquí NO se pide PIN jamás: si el token venció, se
  // BLOQUEA (el lock lo pide) y esta pantalla se re-monta al desbloquear.
  const floorCtx = useFloorActing();
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(!quick); // en quick no se cargan mesas
  const [order, setOrder] = useState<Order | null>(null);      // comanda activa
  const [ticket, setTicket] = useState<Order | null>(null);    // recibo tras cobrar
  const [cancelled, setCancelled] = useState<Order | null>(null); // comprobante de cancelación
  const [showPending, setShowPending] = useState(false);       // §32/A0 — pendientes de entrega
  const [newTableOpen, setNewTableOpen] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);   // error al eliminar mesa
  // PINK #2 — mesa con VARIAS cuentas (cuenta aparte): selector antes de abrir la comanda.
  const [cuentaPick, setCuentaPick] = useState<{ table: Table; orders: Order[] } | null>(null);
  const [activeTableName, setActiveTableName] = useState<string | null>(null); // V49 — para el ticket de cocina

  const brandName = me?.tenant?.branding?.brandName || me?.tenant?.name || 'FluidSol';
  // #249 — sucursal cuyo precio efectivo muestra el menú (== la que cobra la
  // venta): home si el cajero está atado, si no la activa de la sesión, o 'all'
  // (consolidado) para el admin en vista "Todas". En 'all' el server devuelve el
  // precio de catálogo BASE (sin override de ninguna sucursal); antes el menú no
  // mandaba branchId y caía al override de la sucursal DEFAULT.
  const menuBranchId = me?.branch?.homeBranchId ?? me?.branch?.activeBranchId ?? 'all';

  const loadTables = async () => {
    setLoading(true);
    try { setTables((await api('/pos/tables')) as Table[]); }
    finally { setLoading(false); }
  };
  // Abre una venta sin mesa, etiquetada con el módulo (para Insights/Margen/Cierre).
  const startQuickSale = async () => {
    // El acting viaja en el header X-Floor-Acting (api) — nada en el body.
    setOrder((await api('/pos/orders', { method: 'POST', body: { sourceModule } })) as Order);
  };
  // Modo tables → carga mesas; modo quick → abre directo el carrito (en sesión
  // de piso, primero el PIN — la venta rápida también se atribuye).
  useEffect(() => {
    if (quick) {
      if (isFloor) {
        if (floorCtx.token()) void startQuickSale();
        else floorCtx.lock(); // el lock pide el PIN; al desbloquear, re-monta y reintenta
      } else void startQuickSale();
    } else loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🅒 — POLLING del tablero (mismo patrón que cocina, ~7 s): en una demo con tablet +
  // celular, el que NO está capturando ve la mesa cambiar sola. Solo en modo mesas y
  // cuando no hay una comanda abierta en pantalla (esa vista es el carrito, no el tablero).
  useEffect(() => {
    if (quick || order) return;
    // Refresco SILENCIOSO (sin toggle de loading, no parpadea): actualiza el tablero en su lugar.
    const iv = window.setInterval(async () => {
      try { setTables((await api('/pos/tables')) as Table[]); } catch { /* transitorio */ }
    }, 7000);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quick, order]);

  // Eliminar mesa (solo libres; el backend además rechaza mesas con orden abierta).
  const deleteTable = async (t: Table) => {
    if (!window.confirm(`¿Eliminar ${t.name}? Esta acción no se puede deshacer.`)) return;
    setDelErr(null);
    try {
      await api(`/pos/tables/${t.id}`, { method: 'DELETE' });
      loadTables();
    } catch (e: any) {
      setDelErr(e?.body?.message || e?.message || 'No se pudo eliminar la mesa');
    }
  };

  // Abrir/recuperar la comanda de una mesa.
  const openTable = async (t: Table) => {
    // 🅒 — DECISIÓN CERRADA: tocar una mesa AJENA se PERMITE CON AVISO (no se bloquea).
    // La venta se queda atribuida a quien la ABRIÓ (opened_by no cambia; reabrir = getById).
    // Regla de identidad (11/09): en piso, 'mía' = del MESERO del PIN (acting),
    // no del usuario compartido de la sesión — sin esto TODA mesa parecía ajena.
    const myId = (isFloor ? floorCtx.actingUserId() : null) ?? me?.userId;
    if (t.status === 'OCCUPIED' && t.occupiedByUserId && t.occupiedByUserId !== myId) {
      if (!window.confirm(`${t.name} · comanda de ${t.occupiedByName ?? 'otro mesero'} · ¿continuar?`)) return;
    }
    setActiveTableName(t.name); // para el encabezado del ticket de cocina
    if (t.status === 'OCCUPIED') {
      const open = (await api(`/pos/orders?status=OPEN&tableId=${t.id}`)) as Order[];
      // PINK #2 — mesa separada (>1 cuenta): elige cuál cuenta abrir.
      if (open.length > 1) { setCuentaPick({ table: t, orders: open }); return; }
      if (open[0]) { setOrder((await api(`/pos/orders/${open[0].id}`)) as Order); return; }
    }
    // PINK #1 (P1) — orden NUEVA en sesión de piso: PIN primero (atribución).
    const create = async () => {
      // El acting viaja en el header X-Floor-Acting (api) — nada en el body.
      try {
        setOrder((await api('/pos/orders', { method: 'POST', body: { tableId: t.id } })) as Order);
      } catch (e: any) {
        // P2 modo 'owner' — mesa asignada a otro: el backend responde 403. Ofrece
        // teclear el PIN del dueño/encargado (= cambiar mesero → re-abre el lock).
        if (e?.status === 403 && isFloor) {
          if (window.confirm(`${e?.body?.message ?? 'Mesa de otro mesero'} · ¿teclear otro PIN?`)) floorCtx.lock();
          return;
        }
        throw e;
      }
    };
    if (isFloor && !floorCtx.token()) { floorCtx.lock(); return; } // vencido → el lock pide PIN
    await create();
  };

  // PINK #1 (P1) — TODA venta nueva en sesión de piso pasa por el PIN.
  const newQuickSale = () => {
    if (isFloor) {
      if (!floorCtx.token()) { floorCtx.lock(); return; } // vencido → el lock pide PIN
      void startQuickSale();
    } else void startQuickSale();
  };

  // Vista comanda activa
  if (order) {
    return (
      <ComandaPanel
        order={order}
        setOrder={setOrder}
        sourceModule={sourceModule}
        brandName={brandName}
        menuBranchId={menuBranchId}
        tenantId={me?.tenant?.id}
        tableName={activeTableName}
        backLabel={quick ? 'Nueva venta' : 'Volver a mesas'}
        onBack={() => { setOrder(null); if (quick) newQuickSale(); else loadTables(); }}
        onPaid={(paid) => { setOrder(null); setTicket(paid); if (!quick) loadTables(); }}
        onCancelled={(o) => { setOrder(null); setCancelled(o); if (!quick) loadTables(); }}
        // 🅐 pieza 1 — pre-cuenta: LLEVA DIRECTO al ticket, como "Cobrar" (bug del smoke:
        // el early-return de `order` ganaba sobre `ticket` y la vista no aparecía hasta
        // salir de la mesa). Igual que onPaid: cierra la comanda, muestra el ticket y
        // recarga el tablero (la mesa ya se ve violeta al volver).
        onOpenTicket={(o) => { setOrder(null); setTicket(o); if (!quick) loadTables(); }}
      />
    );
  }

  // Ticket tras cobrar. En quick, "Volver" arranca otra venta (flujo de caja continuo).
  if (ticket) {
    return (
      <TicketView
        order={ticket}
        brandName={brandName}
        backLabel={quick ? 'Nueva venta' : 'Volver a mesas'}
        onClose={() => { setTicket(null); if (quick) newQuickSale(); }}
      />
    );
  }

  // §17 Part A fix — comprobante de CANCELACIÓN (NO es una venta): sin total cobrado,
  // sin QR/botón de factura. Una comanda cancelada jamás ofrece facturación.
  if (cancelled) {
    return (
      <CancelledReceiptView
        order={cancelled}
        brandName={brandName}
        tableName={activeTableName}
        backLabel={quick ? 'Nueva venta' : 'Volver a mesas'}
        onClose={() => { setCancelled(null); if (quick) newQuickSale(); }}
      />
    );
  }

  // §32/A0 — pantalla de PENDIENTES DE ENTREGA (mostrador): abre desde el botón del header.
  if (showPending) {
    return (
      <PendingDeliveryView
        onOpenTicket={(o) => { setShowPending(false); setTicket(o); }}
        onClose={() => setShowPending(false)} />
    );
  }

  // Modo quick: no hay grid de mesas; mientras se abre la venta mostramos un loader.
  if (quick) {
    return <p className="text-sm text-slate-500">Abriendo venta rápida…</p>;
  }

  return (
    <div>
      <Header title="Mesas" subtitle="Toca una mesa para abrir o continuar su comanda."
        action={
          <div className="flex flex-wrap gap-2">
            {sourceModule === 'gastro' && (
              <button className={btnGhost} onClick={() => setShowPending(true)}>Pendientes de entrega</button>
            )}
            <button className={btnGhost} onClick={newQuickSale}>Venta rápida</button>
            <button className={btnPrimary} onClick={() => setNewTableOpen(true)}>+ Mesa</button>
          </div>
        } />

      {delErr && (
        <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{delErr}</div>
      )}

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : tables.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
          Aún no hay mesas. Crea la primera con “+ Mesa”.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {tables.map((t) => {
            const occupied = t.status === 'OCCUPIED';
            return (
              // CAUSA REAL del bote desbordado: el grid ESTIRA los wrappers a la fila más
              // alta, pero el botón-tarjeta no llenaba el wrapper → el bote (absolute bottom
              // del wrapper) quedaba flotando DEBAJO de la tarjeta corta. Fix: h-full en el
              // botón (la tarjeta llena el wrapper) + overflow-hidden como recorte de respaldo.
              <div key={t.id} className="relative h-full overflow-hidden rounded-2xl">
                <button onClick={() => openTable(t)}
                  className={`flex h-full w-full flex-col items-start gap-2 rounded-2xl border p-4 text-left transition ${
                    t.awaitingPayment
                      ? 'border-violet-300 bg-violet-50 hover:bg-violet-100' // 🅐 esperando pago
                      : occupied
                        ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                        : 'border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100'
                  }`}>
                  <div className="flex w-full items-center justify-between">
                    <UtensilsCrossed className={`h-5 w-5 ${t.awaitingPayment ? 'text-violet-600' : occupied ? 'text-amber-600' : 'text-emerald-600'}`} />
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      t.awaitingPayment ? 'bg-violet-200 text-violet-800' : occupied ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'
                    }`}>{t.awaitingPayment ? 'Esperando pago' : occupied ? 'Ocupada' : 'Libre'}</span>
                  </div>
                  <span className="font-serif text-lg text-slate-900">{t.name}</span>
                  <span className="text-[11px] text-slate-500">
                    {t.zone ? `${t.zone} · ` : ''}{t.capacity ? `${t.capacity} pers.` : '—'}
                  </span>
                  {/* 🅒 — dueño de la comanda (opened_by). Solo en mesas ocupadas. */}
                  {occupied && t.occupiedByName && (
                    <span className="mt-0.5 truncate text-[11px] font-medium text-amber-700">comanda de {t.occupiedByName}</span>
                  )}
                  {/* P2 — mesero ASIGNADO (color/etiqueta del tablero). Convive con
                      "comanda de X": asignación ≠ quién la abrió. */}
                  {t.assignedUserName && (
                    <span className="mt-0.5 truncate text-[11px] font-medium text-indigo-600">mesa de {t.assignedUserName}</span>
                  )}
                  {/* PINK #2 — cobro parcial: saldo pendiente en el tablero. */}
                  {occupied && t.partialBalance != null && t.partialBalance > 0 && (
                    <span className="mt-0.5 truncate text-[11px] font-semibold text-amber-600">falta {mxn(t.partialBalance)}</span>
                  )}
                  {/* PINK #2 — mesa separada en varias cuentas. */}
                  {occupied && (t.accountCount ?? 1) > 1 && (
                    <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
                      <Layers className="h-3 w-3" /> {t.accountCount} cuentas
                    </span>
                  )}
                </button>
                {/* Eliminar: solo en mesas libres. Una mesa ocupada no se puede
                    borrar (el backend lo rechaza con 400 si tuviera orden abierta). */}
                {!occupied && (
                  <button onClick={(e) => { e.stopPropagation(); deleteTable(t); }}
                    title="Eliminar mesa"
                    className="absolute bottom-2 right-2 rounded-lg p-1.5 text-slate-300 hover:bg-white hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {newTableOpen && (
        <NewTableModal onClose={() => setNewTableOpen(false)} onSaved={() => { setNewTableOpen(false); loadTables(); }} />
      )}

      {/* PINK #2 — selector de cuenta cuando la mesa tiene varias (cuenta aparte). */}
      {cuentaPick && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-serif text-lg text-slate-900">{cuentaPick.table.name} · cuentas</h3>
              <button onClick={() => setCuentaPick(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <p className="mb-3 text-xs text-slate-500">Esta mesa tiene varias cuentas. Elige cuál abrir.</p>
            <div className="space-y-2">
              {cuentaPick.orders.map((o, i) => (
                <button key={o.id}
                  onClick={async () => { const t = cuentaPick.table; setCuentaPick(null); setActiveTableName(t.name); setOrder((await api(`/pos/orders/${o.id}`)) as Order); }}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left hover:bg-slate-50">
                  <span className="text-sm font-medium text-slate-800">
                    {o.customerLabel?.trim() || `Cuenta ${i + 1}`}
                  </span>
                  <span className="tabular-nums text-sm font-semibold text-slate-900">{mxn(o.total)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Comanda: catálogo (izq) + cuenta con desglose IVA (der) + cobro
// ===========================================================================
// V106/V86 — mapea un producto del API (variantes + grupos + precio efectivo por
// sucursal) a un MenuItem del POS. Compartido por el menú y la franja de sugeridos (V130).
function productToMenuItem(p: any): MenuItem {
  return {
    kind: 'product' as const, id: p.id, name: p.name, price: Number(p.effectivePrice ?? p.basePrice) || 0,
    category: p.category || 'Producto',
    // V126 (#310) — paquete prix-fixe: el builder lo etiqueta "menú por tiempos".
    isPrixFixe: !!p.isPrixFixe,
    // available: number | null — null = SIN RASTREO (ilimitado); la píldora se oculta.
    // OJO: `v.available ?? v.onHand` colapsaba ese null a onHand (0) => "agotada" falsa en
    // productos sin rastreo con tallas. Sólo caemos a onHand si el backend NO trae la clave.
    variants: (p.variants ?? []).map((v: any) => ({ id: v.id, name: v.name, price: Number(v.effectivePrice ?? v.price) || 0, available: v.available === undefined ? v.onHand : v.available })),
    // V86 — grupos de modificadores activos (ya vienen adjuntos, sin N+1).
    modifierGroups: (p.modifierGroups ?? []).map((g: any) => ({
      id: g.id, name: g.name, minSelect: Number(g.minSelect) || 0,
      maxSelect: g.maxSelect == null ? null : Number(g.maxSelect),
      required: !!g.required,
      options: (g.options ?? []).map((o: any) => ({ id: o.id, name: o.name, priceDelta: Number(o.priceDelta) || 0 })),
    })),
  };
}

function ComandaPanel({
  order, setOrder, onBack, onPaid, onCancelled, sourceModule = 'gastro', backLabel = 'Volver a mesas',
  brandName = 'FluidSol', menuBranchId = 'all', tenantId, tableName, onOpenTicket,
}: {
  order: Order;
  setOrder: (o: Order) => void;
  onBack: () => void;
  onPaid: (paid: Order) => void;
  onCancelled: (cancelled: Order) => void;
  sourceModule?: 'gastro' | 'boutique' | 'gym';
  backLabel?: string;
  brandName?: string;
  menuBranchId?: string;
  tenantId?: string;
  tableName?: string | null;
  onOpenTicket?: (o: Order) => void; // 🅐 pieza 1 — imprimir pre-cuenta (sin QR)
}) {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [busy, setBusy] = useState(false);
  // TREN 1.1 — candado anti-pisotón: cada mutación LOCAL aplicada (agregar/quitar/
  // editar/enviar) incrementa la secuencia; un poll solo aplica su respuesta si la
  // secuencia no cambió mientras volaba (si no, la descarta — era una foto vieja).
  const mutSeq = useRef(0);
  const busyRef = useRef(false);
  const applyMutation = (o: Order) => { mutSeq.current += 1; setOrder(o); };
  // 🔴 Carrera envío-vs-captura (4.º intento): cada creación de renglón se
  // persiste con su propia petición (~350 ms). El tracker cuenta las capturas EN
  // VUELO; "Enviar a cocina" espera a que TODAS confirmen antes de /fire (el
  // `busy` booleano se apagaba al resolver la primera y re-habilitaba Enviar).
  const captures = useRef(new CaptureTracker()).current;
  const [capturing, setCapturing] = useState(0);
  useEffect(() => captures.subscribe(() => setCapturing(captures.pending())), [captures]);
  /** Envuelve una captura (POST/PATCH/DELETE de renglón) para el tracker + applyMutation. */
  const capture = (p: Promise<Order>) => captures.track(p.then((o) => { applyMutation(o); return o; }));
  const [showTax, setShowTax] = useState(true);
  const [cobroOpen, setCobroOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false); // §17 Part A — cancelar comanda
  const [splitAcctOpen, setSplitAcctOpen] = useState(false); // PINK #2 — pasar a cuenta aparte
  const [cat, setCat] = useState('Todo'); // categoría activa del menú
  const [q, setQ] = useState('');         // búsqueda por nombre (como en las listas)
  const [variantPick, setVariantPick] = useState<MenuItem | null>(null); // §21.2 — elegir variante
  // V86 — builder "Arma tu platillo" (alta) y re-armado de una línea existente (edición).
  const [builderPick, setBuilderPick] = useState<MenuItem | null>(null);
  const [builderEdit, setBuilderEdit] = useState<{ item: OrderItem; product: MenuItem } | null>(null);

  // Catálogo por módulo: Wellness (gym) vende solo productos; Boutique y Gastro,
  // servicios + productos. Comparten el catálogo del tenant (/boutique/*).
  const withServices = sourceModule !== 'gym';
  const catalogTitle = sourceModule === 'gastro' ? 'Menú' : 'Catálogo';

  useEffect(() => {
    (async () => {
      const [svcs, prods] = await Promise.all([
        withServices ? api('/boutique/services').catch(() => []) : Promise.resolve([]),
        // V106 — precio efectivo por sucursal (override si existe): el menú y el
        // preview muestran EXACTAMENTE lo que cobra la venta, sin flip base→override.
        // #249 — se manda la sucursal en vista (home/activa, o 'all' = consolidado →
        // catálogo base): así el override de una sucursal NO se ve en otra ni en "Todas".
        api(`/boutique/products?withBranchPrice=true&branchId=${encodeURIComponent(menuBranchId)}`).catch(() => []),
      ]);
      const m: MenuItem[] = [
        ...(svcs as any[]).map((s) => ({ kind: 'service' as const, id: s.id, name: s.name, price: Number(s.defaultPrice) || 0, category: s.category || 'Servicio' })),
        ...(prods as any[]).map(productToMenuItem),
      ];
      setMenu(m);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withServices, menuBranchId]);

  // V130 (§31.4) — franja "Sugeridos" del POS (upsell). Solo GastroGest; el backend
  // devuelve máx 4 DISPONIBLES en la sucursal de venta (fail-closed por stock/insumos,
  // G10) → si no hay marcados/disponibles, la franja no se pinta y el POS es idéntico
  // al de hoy. Mismo shape que el menú → reusa addItem sin ramas nuevas.
  const [suggested, setSuggested] = useState<MenuItem[]>([]);
  useEffect(() => {
    if (sourceModule !== 'gastro') { setSuggested([]); return; }
    let cancelled = false;
    (async () => {
      const prods = await api(`/gastro/suggested?branchId=${encodeURIComponent(menuBranchId)}`).catch(() => []);
      if (!cancelled) setSuggested((prods as any[]).map(productToMenuItem));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceModule, menuBranchId]);

  // Categorías presentes en el menú (+ "Todo"). Comida/Bebida primero para que en
  // un restaurante la pestaña relevante quede al frente.
  const categories = useMemo(() => {
    const order = ['Comida', 'Bebida', 'Servicio', 'Producto'];
    const present = Array.from(new Set(menu.map((m) => m.category))).sort(
      (a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99) || a.localeCompare(b),
    );
    return ['Todo', ...present];
  }, [menu]);

  const filteredMenu = useMemo(() => {
    const base = cat === 'Todo' ? menu : menu.filter((m) => m.category === cat);
    const needle = q.trim().toLowerCase();
    return needle ? base.filter((m) => m.name.toLowerCase().includes(needle)) : base;
  }, [menu, cat, q]);

  const addItem = async (it: MenuItem) => {
    // V86 — producto con grupos de modificadores → builder touch (maneja también
    // la variante como primera sección). Sin grupos pero con variantes → picker
    // §21.2 de siempre (cero regresión).
    if (it.kind === 'product' && it.modifierGroups && it.modifierGroups.length > 0) {
      setBuilderPick(it);
      return;
    }
    // §21.2 — si el producto tiene variantes, primero elegir una (el backend la exige).
    if (it.kind === 'product' && it.variants && it.variants.length > 0) {
      setVariantPick(it);
      return;
    }
    setBusy(true);
    try {
      const body = it.kind === 'product' ? { productId: it.id, qty: 1 } : { serviceId: it.id, qty: 1 };
      await capture(api(`/pos/orders/${order.id}/items`, { method: 'POST', body }) as Promise<Order>);
    } finally { setBusy(false); }
  };

  // V205 (D3) — "lo de siempre": agrega con un toque el producto (y la variante) que el
  // miembro compra seguido. Si el producto pide armarse (modificadores), abre el builder.
  const [promoTicket, setPromoTicket] = useState<Order | null>(null);
  const addUsual = async (u: { productId: string; variantId: string | null }) => {
    const mi = menu.find((m) => m.kind === 'product' && m.id === u.productId);
    if (mi?.modifierGroups?.length) { setBuilderPick(mi); return; }
    setBusy(true);
    try {
      const body = { productId: u.productId, qty: 1, ...(u.variantId ? { variantId: u.variantId } : {}) };
      await capture(api(`/pos/orders/${order.id}/items`, { method: 'POST', body }) as Promise<Order>);
    } catch (e: any) {
      setFireMsg(e?.body?.message || e?.message || 'No se pudo agregar el producto');
    } finally { setBusy(false); }
  };

  // V86 — confirma el armado: el server re-valida min/max y resuelve precios/costos.
  const addBuiltDish = async (
    product: MenuItem, sel: { variantId?: string; qty: number; modifierOptionIds: string[] },
  ) => {
    setBusy(true);
    try {
      await capture(api(`/pos/orders/${order.id}/items`, {
        method: 'POST',
        body: {
          productId: product.id,
          ...(sel.variantId ? { variantId: sel.variantId } : {}),
          qty: sel.qty,
          modifierOptionIds: sel.modifierOptionIds,
        },
      }) as Promise<Order>);
      setBuilderPick(null);
    } finally { setBusy(false); }
  };

  // V86 — re-armado de una línea existente (replace SOLO de los modificadores de
  // catálogo; la nota y los extras ad-hoc del lápiz no se tocan).
  const saveBuiltDish = async (itemId: string, modifierOptionIds: string[]) => {
    setBusy(true);
    try {
      applyMutation((await api(`/pos/orders/${order.id}/items/${itemId}`, {
        method: 'PATCH', body: { modifierOptionIds },
      })) as Order);
      setBuilderEdit(null);
    } finally { setBusy(false); }
  };

  // §21.2 — agrega el producto con la variante elegida (snapshot server-side).
  const addVariant = async (product: MenuItem, variant: Variant) => {
    setBusy(true);
    try {
      await capture(api(`/pos/orders/${order.id}/items`, {
        method: 'POST', body: { productId: product.id, variantId: variant.id, qty: 1 },
      }) as Promise<Order>);
      setVariantPick(null);
    } finally { setBusy(false); }
  };

  const removeItem = async (itemId: string) => {
    setBusy(true);
    try { applyMutation((await api(`/pos/orders/${order.id}/items/${itemId}`, { method: 'DELETE' })) as Order); }
    finally { setBusy(false); }
  };

  // V48 — guarda nota + extras de un ítem ya en la comanda (replace de extras server-side).
  const [editItem, setEditItem] = useState<OrderItem | null>(null);
  const saveItemMods = async (itemId: string, notes: string, modifiers: ItemModifier[]) => {
    setBusy(true);
    try {
      applyMutation((await api(`/pos/orders/${order.id}/items/${itemId}`, {
        method: 'PATCH',
        body: { notes, modifiers: modifiers.map((m) => ({ name: m.name, price: m.price })) },
      })) as Order);
      setEditItem(null);
    } finally { setBusy(false); }
  };

  // V49 — ticket de cocina: imprime SOLO los ítems pendientes (no enviados) + mesa +
  // notas/extras, sin precios; luego los sella (fired_at) para que el próximo envío
  // imprima solo lo nuevo. QZ Tray (silencioso) con fallback a browser-print.
  const [printerOpen, setPrinterOpen] = useState(false);
  // V117 — asignar mesa / comensales a la orden abierta (venta rápida → mesa).
  const [tableOpen, setTableOpen] = useState(false);
  const [fireMsg, setFireMsg] = useState<string | null>(null);
  const kitchenLabel = tableName ? `Mesa: ${tableName}` : (order.customerLabel || 'Venta rápida');
  const pending = order.items.filter((it) => !it.firedAt);
  // 🔴 Corrección 11/09 — el BATCH lo decide el SERVIDOR: /fire sella TODOS los
  // pendientes reales con UN timestamp (ignora al cliente). Ningún estado viejo,
  // poll, ni bundle desactualizado puede volver a partir la comanda. El ticket
  // se imprime DEL RESPONSE (lo sellado == lo impreso, batchJustFired).
  const sendToKitchen = async () => {
    setBusy(true); busyRef.current = true; setFireMsg(null);
    try {
      // 🔴 Carrera envío-vs-captura (fix 4.º intento): ESPERA a que TODAS las
      // capturas en vuelo estén confirmadas en la base ANTES de /fire. Sin esto,
      // /fire sellaba solo lo persistido (a veces 1 renglón). El botón ya mostró
      // "Guardando…" mientras `capturing > 0`; aquí drenamos por si el clic ganó.
      if (captures.pending() > 0) { setFireMsg('Guardando capturas…'); await captures.whenIdle(); }
      const updated = (await api(`/pos/orders/${order.id}/fire`, {
        method: 'POST', body: {},
      })) as Order;
      applyMutation(updated);
      const batch = batchJustFired(updated.items as any) as unknown as OrderItem[];
      if (batch.length === 0) { setFireMsg('Nada pendiente por enviar.'); return; }
      const at = new Date();
      // 🔴 Fix cocina (diagnóstico 11/09) — la comanda se imprime SOLO si hay
      // impresora de cocina activa Y el toggle no está en false. SIN impresora,
      // "Enviar a cocina" YA NO abre el diálogo del navegador (era el artefacto
      // que se veía "por platillo"): solo manda a la pantalla y lo dice.
      const branchIdForPrint = (user as any)?.branch?.activeBranchId ?? null;
      const [printers, tset] = await Promise.all([
        getPrintersFor(branchIdForPrint).catch(() => []),
        fetchTicketSettings(branchIdForPrint, true).catch(() => null),
      ]);
      const decision = decideKitchenPrint(printers, (tset as any)?.printComanda);
      let printedOk = false;
      if (decision.print) {
        const comandaItems = batch.map((it) => ({
          qty: it.qty, description: it.description, notes: it.notes,
          // V86 — cocina lee la composición con su grupo: "Salsa: Verde" / "+ Queso".
          modifiers: (it.modifiers ?? []).map((m) => ({
            name: m.groupName ? `${m.groupName}: ${m.name}` : m.name,
          })),
        }));
        const res = await printTicket({
          tenantId,
          branchId: branchIdForPrint,
          kind: 'comanda',
          escpos: (w) => kitchenTicket({ brandName, tableLabel: kitchenLabel, at, items: comandaItems, cols: w === 58 ? 32 : 48 }),
          html: (w) => comandaHtml({ brandName, tableLabel: kitchenLabel, at, items: comandaItems }, w),
        });
        printedOk = !(res.usedFallback || res.method === 'browser');
        if (!printedOk) {
          setFireMsg(`⚠ No imprimió en la impresora de COCINA. ${res.error ?? ''}`);
          return;
        }
      }
      // Sin impresora / toggle off → "sin impresión" (la comanda ya está en la pantalla de cocina).
      setFireMsg(kitchenSentMessage(batch.length, printedOk));
    } catch (e: any) {
      setFireMsg(e?.body?.message || e?.message || 'No se pudo enviar a cocina.');
    } finally { setBusy(false); busyRef.current = false; }
  };

  // V205 (decisión Arturo) — la cortesía de fidelidad en GastroGest entra a cocina/barra
  // como cualquier platillo: el backend ya la DISPARÓ (pantalla de cocina); aquí se imprime
  // su comanda con la MISMA regla que "Enviar a cocina" (solo con impresora de cocina activa).
  const printCourtesyComanda = async (promo: Order) => {
    if (sourceModule !== 'gastro' || !promo.fulfillmentStatus) return;
    const label = `Cortesía fidelidad${promo.deliveryCode ? ` #${promo.deliveryCode}` : ''}`;
    try {
      const branchIdForPrint = (user as any)?.branch?.activeBranchId ?? null;
      const [printers, tset] = await Promise.all([
        getPrintersFor(branchIdForPrint).catch(() => []),
        fetchTicketSettings(branchIdForPrint, true).catch(() => null),
      ]);
      const decision = decideKitchenPrint(printers, (tset as any)?.printComanda);
      if (!decision.print) { setFireMsg(`${label}: enviada a la pantalla de cocina.`); return; }
      const at = new Date();
      const items = promo.items.map((it) => ({ qty: it.qty, description: it.description, notes: it.notes, modifiers: [] }));
      const res = await printTicket({
        tenantId, branchId: branchIdForPrint, kind: 'comanda',
        escpos: (w) => kitchenTicket({ brandName, tableLabel: label, at, items, cols: w === 58 ? 32 : 48 }),
        html: (w) => comandaHtml({ brandName, tableLabel: label, at, items }, w),
      });
      setFireMsg(res.usedFallback || res.method === 'browser'
        ? `⚠ ${label}: no imprimió en la impresora de COCINA. ${res.error ?? ''}`
        : `${label}: comanda enviada a cocina.`);
    } catch (e: any) {
      setFireMsg(e?.message || 'No se pudo imprimir la comanda de la cortesía.');
    }
  };

  // 🔴 Corrección 11/09 — el lock NO se auto-dispara con captura viva: mientras
  // haya renglones SIN ENVIAR o un modal abierto, se retiene el candado del
  // auto-lock (perder captura por un lock es inaceptable). El botón Bloquear
  // manual sigue funcionando (lock() limpia los holds).
  const floorLockCtx = useFloorActing();
  const lockHold = pending.length > 0 || cobroOpen || cancelOpen || tableOpen || printerOpen
    || splitAcctOpen || !!editItem || !!builderPick || !!builderEdit || !!variantPick;
  useEffect(() => {
    if (!lockHold) return;
    floorLockCtx.holdLock(true);
    return () => floorLockCtx.holdLock(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockHold]);
  const title = order.tableId ? 'Comanda de mesa' : 'Venta rápida';

  // §31.2 (V131) — semáforo de la comanda (solo gastro): mide el pendiente (enviado a
  // cocina y aún no listo) MÁS VIEJO contra el umbral del tenant. Tick local (1s) para
  // avanzar el reloj + polling (G5, 5–10s) para reflejar los "listos" que marca cocina.
  const { user } = useAuth();
  const canCancelOrder = user?.role === 'SUPER_USER'
    || (Array.isArray(user?.permissions) && user!.permissions.includes('pos:delete'));
  const kitchenThreshold = clampKitchenAlertMinutes(user?.tenant?.kitchenAlertMinutes);
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    if (sourceModule !== 'gastro') return;
    const tick = window.setInterval(() => setNowTs(Date.now()), 1000);
    // TREN 1.1 — el poll YA NO pisa capturas: no corre con una mutación en vuelo
    // (busy) y su respuesta se DESCARTA si hubo cualquier mutación local mientras
    // volaba (applyPollResponse compara la secuencia). Era la causa del "un envío
    // por platillo": una foto vieja borraba renglones de la vista.
    const poll = window.setInterval(async () => {
      if (busyRef.current) return;
      const seqAt = mutSeq.current;
      try {
        const fresh = (await api(`/pos/orders/${order.id}`)) as Order;
        // Si hubo CUALQUIER mutación local mientras el poll volaba, la foto es
        // vieja y se tira; si no, el estado actual == el de la foto y aplicarla
        // es seguro (trae los "listos" que marcó cocina).
        if (pollStillValid(seqAt, mutSeq.current)) setOrder(fresh);
      } catch { /* el próximo poll corrige */ }
    }, 7000);
    return () => { window.clearInterval(tick); window.clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceModule, order.id]);
  const oldestPendingFiredMs = (() => {
    let oldest: number | null = null;
    for (const it of order.items) {
      if (it.firedAt && !it.readyAt) {
        const t = new Date(it.firedAt).getTime();
        if (Number.isFinite(t) && (oldest === null || t < oldest)) oldest = t;
      }
    }
    return oldest;
  })();
  const semAge = oldestPendingFiredMs !== null ? Math.max(0, Math.floor((nowTs - oldestPendingFiredMs) / 1000)) : null;
  const semColor = semAge !== null ? kitchenColor(semAge, kitchenThreshold) : null;

  const markReady = async (itemId: string, ready: boolean) => {
    setBusy(true);
    try {
      setOrder((await api(`/pos/kitchen/orders/${order.id}/items/${itemId}/ready`, {
        method: 'POST', body: { ready },
      })) as Order);
    } catch (e: any) { setFireMsg(e?.body?.message || e?.message || 'No se pudo marcar el platillo.'); }
    finally { setBusy(false); }
  };
  const nudgeKitchen = async () => {
    setBusy(true); setFireMsg(null);
    try {
      setOrder((await api(`/pos/kitchen/orders/${order.id}/nudge`, { method: 'POST' })) as Order);
      setFireMsg('Se presionó a cocina.');
    } catch (e: any) { setFireMsg(e?.body?.message || e?.message || 'No se pudo presionar a cocina.'); }
    finally { setBusy(false); }
  };

  return (
    // pb-24 md:pb-0: deja hueco abajo en móvil para la barra fija de cobro (Tarea 2),
    // que si no taparía el último producto/botón.
    <div className="pb-24 md:pb-0">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> {backLabel}
      </button>

      {/* responsive-turno: las dos columnas arrancan en md: (tablet vertical), no en
          lg:, para que la cuenta quede junto al catálogo y el mesero NO tenga que
          scrollear todo el menú para llegar a Cobrar. A 768px el sidebar estático
          (Dashboard.tsx, w-72) deja ~400px de contenido, así que a md: repartimos 6/6
          (no 7/5) para que la cuenta no quede impracticable; en lg:, donde ya hay
          ancho, volvemos al 7/5 original. */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
        {/* Catálogo */}
        <section className="md:col-span-6 lg:col-span-7">
          <h3 className="mb-3 font-serif text-lg text-slate-900">{catalogTitle}</h3>
          {/* V130 (§31.4) — franja "Sugeridos" (upsell). Máx 4; solo se pinta si hay
              sugeridos DISPONIBLES en la sucursal → sin marcados, el POS es idéntico
              al de hoy. Sin aviso permanente en la pantalla de cobro (va en Config y
              cierre de día). Reusa addItem (builder/variantes/directo). */}
          {suggested.length > 0 && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                <Sparkles className="h-3.5 w-3.5" /> Sugeridos
              </p>
              <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                {suggested.map((it) => (
                  <button key={`sug-${it.id}`} disabled={busy} onClick={() => addItem(it)}
                    className="flex flex-col items-start gap-1 rounded-xl border border-amber-200 bg-white p-3 text-left hover:bg-amber-50 disabled:opacity-50">
                    <span className="text-sm font-medium text-slate-800">{it.name}</span>
                    {it.variants && it.variants.length > 0
                      ? <span className="text-[11px] font-medium text-slate-400">elegir tamaño · desde {mxn(Math.min(...it.variants.map((v) => v.price)))}</span>
                      : <span className="text-xs tabular-nums text-slate-500">{mxn(it.price)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Búsqueda por nombre (mismo patrón que las listas de catálogo). */}
          <input className="input mb-3" placeholder="Buscar por nombre…" value={q}
            onChange={(e) => setQ(e.target.value)} />
          {/* Filtro por categoría (un restaurante elige "Comida") */}
          {categories.length > 2 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button key={c} onClick={() => setCat(c)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${cat === c ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          {menu.length === 0 ? (
            <p className="text-sm text-slate-400">Sin productos/servicios en el catálogo todavía.</p>
          ) : filteredMenu.length === 0 ? (
            <p className="text-sm text-slate-400">Sin ítems en “{cat}”.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
              {filteredMenu.map((it) => (
                <button key={`${it.kind}-${it.id}`} disabled={busy} onClick={() => addItem(it)}
                  className="flex flex-col items-start gap-1 rounded-xl border border-slate-200 bg-white p-3 text-left hover:bg-slate-50 disabled:opacity-50">
                  <span className="text-sm font-medium text-slate-800">{it.name}</span>
                  {it.variants && it.variants.length > 0
                    ? <span className="text-[11px] font-medium text-slate-400">elegir tamaño · desde {mxn(Math.min(...it.variants.map((v) => v.price)))}</span>
                    : <span className="text-xs tabular-nums text-slate-500">{mxn(it.price)}</span>}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Cuenta */}
        <section className="md:col-span-6 lg:col-span-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-lg text-slate-900">{title}</h3>
                {/* V117 follow-up — comensales SIEMPRE a la vista junto a la comanda
                    (el cajero/mesero no lo olvida); un tap abre el modal para editar.
                    Ámbar cuando falta capturar; slate cuando ya está. */}
                <button onClick={() => setTableOpen(true)}
                  title={order.diners ? `${order.diners} comensales — tap para editar` : 'Capturar comensales'}
                  className={`inline-flex min-h-8 items-center gap-1 rounded-full px-2.5 text-xs font-semibold tabular-nums ${
                    order.diners ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
                  <Users className="h-3.5 w-3.5" /> {order.diners ?? '+'}
                </button>
                {/* §31.2 — semáforo de la comanda: cronómetro del pendiente más viejo,
                    verde→ámbar→rojo vs el umbral del tenant. Solo gastro y solo si hay
                    algo en cocina sin marcar listo. */}
                {sourceModule === 'gastro' && semColor && semAge !== null && (
                  <span title={`Pendiente más viejo en cocina (umbral ${kitchenThreshold} min)`}
                    className={`inline-flex min-h-8 items-center gap-1 rounded-full px-2.5 text-xs font-semibold tabular-nums ${KITCHEN_TEXT[semColor]}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${KITCHEN_DOT[semColor]}`} />
                    {fmtElapsed(semAge)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPrinterOpen(true)} title="Impresora de esta estación"
                  className="text-slate-300 hover:text-slate-700">
                  <Printer className="h-4 w-4" />
                </button>
                <Receipt className="h-5 w-5 text-slate-400" />
              </div>
            </div>

            {/* V117 — mesa + comensales: si el comensal decide quedarse, la venta rápida
                se pasa a una mesa SIN re-capturar (misma orden). Comensales opcional. */}
            <button onClick={() => setTableOpen(true)}
              className="mb-3 inline-flex min-h-10 items-center gap-1.5 rounded-xl bg-slate-100 px-3 text-xs font-medium text-slate-700 hover:bg-slate-200">
              <Users className="h-3.5 w-3.5" />
              {order.tableId ? 'Mesa asignada' : 'Asignar mesa'}
              {order.diners ? ` · ${order.diners} ${order.diners === 1 ? 'persona' : 'personas'}` : ''}
            </button>

            {order.items.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Agrega productos del menú.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {groupOrderItems(order.items).map((g) => {
                  const it = g.rep; // unidad representativa (todas idénticas)
                  return (
                  <li key={it.id} className="py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700">
                        <span className="tabular-nums text-slate-400">{g.qty}×</span> {it.description}
                        {/* §17 Part A — el renglón salió: "listo" (platillo de cocina) o
                            "entregada" (bebida/embotellado entregada directo, sin fire). */}
                        {it.readyAt ? (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10px] font-medium text-slate-400" title={it.firedAt ? 'Listo en cocina' : 'Entregada'}>
                            <Check className="h-3 w-3" /> {it.firedAt ? 'listo' : 'entregada'}
                          </span>
                        ) : it.firedAt ? (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10px] font-medium text-emerald-600" title="Enviado a cocina">
                            <ChefHat className="h-3 w-3" /> en cocina
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="tabular-nums text-slate-800">{mxn(g.lineTotal)}</span>
                        {/* §31.2 — marcar listo (un toque) los renglones ya en cocina y aún
                            pendientes. Sale del cálculo del semáforo. Solo gastro. */}
                        {sourceModule === 'gastro' && it.firedAt && !it.readyAt && (
                          <button onClick={() => markReady(it.id, true)} disabled={busy} title="Marcar listo"
                            className="text-slate-300 hover:text-emerald-600 disabled:opacity-50">
                            <Check className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* §17 Part A — ENTREGA DIRECTA: un producto listo-para-servir
                            (bebida/embotellado, sin receta) no pasa por cocina; el mesero/
                            cajero lo marca "entregada" aquí y su inventario baja en ese
                            momento. Solo gastro, solo si no requiere cocina y aún no salió. */}
                        {sourceModule === 'gastro' && it.requiresKitchen === false && !it.readyAt && (
                          <button onClick={() => markReady(it.id, true)} disabled={busy} title="Entregar (baja inventario)"
                            className="rounded-lg bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                            Entregar
                          </button>
                        )}
                        {/* V86 — re-armar el platillo (solo líneas de productos con grupos).
                            Edita/quita SOLO una unidad del grupo → si cambia, se separa en su
                            propia línea (los fires/conteo de cocina no se tocan). */}
                        {(() => {
                          const prod = it.productId
                            ? menu.find((m) => m.kind === 'product' && m.id === it.productId)
                            : undefined;
                          return prod?.modifierGroups?.length ? (
                            <button onClick={() => setBuilderEdit({ item: it, product: prod })} title="Re-armar platillo"
                              className="text-slate-300 hover:text-slate-700">
                              <Layers className="h-3.5 w-3.5" />
                            </button>
                          ) : null;
                        })()}
                        <button onClick={() => setEditItem(it)} title="Nota / extras"
                          className="text-slate-300 hover:text-slate-700">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {/* Quita UNA unidad (la última agregada del grupo) → "2×" pasa a "1×". */}
                        <button onClick={() => removeItem(g.ids[g.ids.length - 1])} title={g.qty > 1 ? 'Quitar una unidad' : 'Quitar'}
                          className="text-slate-300 hover:text-rose-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                    {/* V48 — nota y extras debajo del ítem. */}
                    <ItemMods item={it} />
                  </li>
                  );
                })}
              </ul>
            )}

            {/* Totales con desglose IVA (server-side) */}
            <div className="mt-4 border-t border-slate-100 pt-3">
              <button onClick={() => setShowTax((s) => !s)}
                className="mb-1 text-[11px] font-medium text-slate-400 hover:text-slate-600">
                {showTax ? 'Ocultar IVA' : 'Mostrar IVA'}
              </button>
              {showTax && (
                <>
                  <Row label="Subtotal" value={mxn(order.subtotal)} muted />
                  <Row label="IVA (16%)" value={mxn(order.taxTotal)} muted />
                </>
              )}
              <Row label="Total" value={mxn(order.total)} strong />
            </div>

            {/* V49 — enviar a cocina (solo gastro): imprime el delta de ítems nuevos. */}
            {sourceModule === 'gastro' && (
              <button disabled={busy || capturing > 0 || pending.length === 0} onClick={sendToKitchen}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                <ChefHat className="h-4 w-4" />
                {/* 🔴 mientras haya capturas en vuelo, "Guardando…" (el mesero VE por
                    qué espera — nunca se dispara /fire con renglones a medio guardar). */}
                {capturing > 0
                  ? `Guardando… (${capturing})`
                  : pending.length === 0
                    ? 'Todo enviado a cocina'
                    : `Enviar a cocina (${pending.length} ${pending.length === 1 ? 'nuevo' : 'nuevos'})`}
              </button>
            )}
            {/* §31.2 — presionar a cocina: señal de urgencia (quién y cuándo). Solo si
                hay algo pendiente en cocina. No cambia el estado de los platillos. */}
            {sourceModule === 'gastro' && semColor && (
              <button disabled={busy} onClick={nudgeKitchen}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                <Bell className="h-4 w-4" /> Presionar a cocina
              </button>
            )}
            {fireMsg && <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-600">{fireMsg}</p>}

            {/* #8 (smoke 08/09) — mesa ocupada SIN comanda: "Liberar mesa" (0 renglones;
                no hay venta que cancelar → sin pos:delete; el backend exige ser quien
                abrió o admin). Además el tablero auto-libera comandas vacías a los 10 min. */}
            {order.items.length === 0 && order.tableId && (
              <button disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try { await api(`/pos/orders/${order.id}/release`, { method: 'POST' }); onBack(); }
                  catch (e: any) { setFireMsg(e?.body?.message || e?.message || 'No se pudo liberar la mesa'); }
                  finally { setBusy(false); }
                }}
                className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                Liberar mesa (sin consumo)
              </button>
            )}
            {/* V205 — Fidelidad (flujo A): el código se captura ANTES de cobrar; al cobrar
                pagada la cuenta suma UN sello. Se oculta si el programa está apagado. */}
            <LoyaltyPosBox order={order} sourceModule={sourceModule} disabled={busy}
              onOrder={(o) => applyMutation(o as Order)}
              onAddUsual={addUsual}
              onPromoTicket={(o) => { setPromoTicket(o as Order); void printCourtesyComanda(o as Order); }} />
            {/* 🅐 pieza 1 — "Imprimir pre-cuenta" va ARRIBA de "Cobrar": es el orden real de
                operación (primero se lleva la cuenta, después se cobra). Marca "esperando
                pago" y LLEVA DIRECTO al ticket (sin QR — la orden abierta no tiene CFDI). */}
            {sourceModule === 'gastro' && order.status === 'OPEN' && (
              <button disabled={busy || order.items.length === 0}
                onClick={async () => {
                  const next = !order.awaitingPaymentSince;
                  setBusy(true);
                  try {
                    const o = await api(`/pos/orders/${order.id}/awaiting-payment`, { method: 'POST', body: { awaiting: next } }) as Order;
                    // Marcar → directo al ticket de pre-cuenta (el parent cierra la comanda);
                    // cancelar → la comanda sigue abierta con el estado limpio.
                    if (next && onOpenTicket) onOpenTicket(o); else setOrder(o);
                  } catch (e: any) { setFireMsg(e?.body?.message || e?.message || 'No se pudo marcar la pre-cuenta'); } // cero window.alert (regla)
                  finally { setBusy(false); }
                }}
                className={`mt-3 w-full rounded-xl border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                  order.awaitingPaymentSince
                    ? 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}>
                {order.awaitingPaymentSince ? 'Cancelar pre-cuenta (volver a abierta)' : 'Imprimir pre-cuenta'}
              </button>
            )}
            <button disabled={order.items.length === 0} onClick={() => setCobroOpen(true)}
              className="mt-2 w-full rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
              Cobrar {mxn(order.total)}
            </button>
            {/* PINK #2 — PASAR A CUENTA APARTE: solo mesas con 2+ renglones (mueve N a una
                cuenta nueva de la misma mesa; cada cuenta cobra/factura por separado). */}
            {order.tableId && order.items.length > 1 && (
              <button onClick={() => setSplitAcctOpen(true)}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-white px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-50">
                <Layers className="h-4 w-4" /> Pasar a cuenta aparte
              </button>
            )}
            {/* §17 Part A — cancelar comanda (pos:delete). Si hay productos ya entregados
                que controlan inventario, el modal FUERZA elegir íntegro/dañado (anti-fraude). */}
            {canCancelOrder && (
              <button onClick={() => setCancelOpen(true)}
                className="mt-2 w-full rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50">
                Cancelar comanda
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Tarea 2 — barra fija de cobro SOLO en móvil (< md). A 375px no caben dos
          columnas, así que el mesero toma la orden con el catálogo a pantalla completa;
          esta barra mantiene el total y "Cobrar" siempre a la vista sin scrollear hasta
          la cuenta, y abre el cobro directo. Solo con ítems en la orden. */}
      {order.items.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 p-3 backdrop-blur md:hidden">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">Total</p>
              <p className="truncate text-lg font-semibold tabular-nums text-slate-900">{mxn(order.total)}</p>
            </div>
            <button onClick={() => setCobroOpen(true)}
              className="shrink-0 rounded-xl bg-[var(--brand-primary)] px-6 py-3 text-sm font-semibold text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]">
              Cobrar
            </button>
          </div>
        </div>
      )}

      {/* §17 Part A — cancelación con decisión anti-fraude por producto entregado. */}
      {cancelOpen && (
        <CancelOrderModal order={order}
          onClose={() => setCancelOpen(false)}
          onCancelled={(o) => { setCancelOpen(false); onCancelled(o); }} />
      )}

      {/* V205 — ticket de PROMOCIÓN (canje): documento aparte; la cuenta sigue abierta. */}
      {promoTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4">
          <div className="mx-auto max-w-md rounded-2xl bg-slate-50 p-4">
            <TicketView order={promoTicket} brandName={brandName} onClose={() => setPromoTicket(null)} backLabel="Volver a la cuenta" />
          </div>
        </div>
      )}

      {cobroOpen && (
        <CobroModal order={order} sourceModule={sourceModule} onClose={() => setCobroOpen(false)} onPaid={onPaid}
          onPartial={(o) => { setCobroOpen(false); setOrder(o); }} />
      )}

      {/* PINK #2 — pasar renglones a una cuenta aparte (nueva orden en la misma mesa). */}
      {splitAcctOpen && (
        <SplitAccountModal order={order}
          onClose={() => setSplitAcctOpen(false)}
          onSplit={(source) => { setSplitAcctOpen(false); setOrder(source); }} />
      )}

      {/* V49 — selección de impresora de esta estación (QZ Tray). */}
      {printerOpen && (
        <PrinterSettingsModal tenantId={tenantId} onClose={() => setPrinterOpen(false)} />
      )}

      {/* V117 — asignar/mover/quitar mesa + comensales (orden ABIERTA). */}
      {tableOpen && (
        <AssignTableModal order={order}
          onClose={() => setTableOpen(false)}
          onSaved={(o) => { setOrder(o); setTableOpen(false); }} />
      )}

      {/* V48 — nota libre + extras con precio de un ítem de la comanda. */}
      {editItem && (
        <ItemModsModal item={editItem} busy={busy}
          onClose={() => setEditItem(null)} onSave={saveItemMods} />
      )}

      {/* V86 — builder touch "Arma tu platillo" (alta). */}
      {builderPick && (
        <DishBuilderModal
          product={builderPick}
          busy={busy}
          onClose={() => setBuilderPick(null)}
          onConfirm={(sel) => addBuiltDish(builderPick, sel)}
        />
      )}
      {/* V86 — re-armado de una línea existente (variante y qty quedan fijas). */}
      {builderEdit && (
        <DishBuilderModal
          product={builderEdit.product}
          busy={busy}
          editItem={builderEdit.item}
          onClose={() => setBuilderEdit(null)}
          onConfirm={(sel) => saveBuiltDish(builderEdit.item.id, sel.modifierOptionIds)}
        />
      )}

      {/* §21.2 — picker de variante/tamaño antes de agregar a la comanda. */}
      {variantPick && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={() => setVariantPick(null)}>
          <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-slate-900">{variantPick.name}</h3>
            <p className="mb-3 text-xs text-slate-500">Elige el tamaño / variante.</p>
            <div className="space-y-2">
              {(variantPick.variants ?? []).map((v) => (
                <button key={v.id} disabled={busy} onClick={() => addVariant(variantPick, v)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-2.5 text-left hover:bg-slate-50 disabled:opacity-50">
                  <span className="text-sm font-medium text-slate-800">{v.name}</span>
                  <span className="flex items-center gap-1.5 text-sm tabular-nums text-slate-600">
                    {mxn(v.price)}
                    {v.available != null && (
                      <span className={`rounded px-1 text-[10px] font-semibold ${v.available <= 0 ? 'bg-rose-100 text-rose-700' : v.available <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {v.available <= 0 ? 'agotada' : `${v.available} disp`}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button className="rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200" onClick={() => setVariantPick(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'mt-1 text-base font-semibold text-slate-900' : 'text-sm'} ${muted ? 'text-slate-500' : ''}`}>
      <span>{label}</span><span className="tabular-nums">{value}</span>
    </div>
  );
}

// ===========================================================================
// §17 Part A — CANCELAR comanda con DECISIÓN ANTI-FRAUDE. Todo producto YA ENTREGADO
// que controla inventario (bebida/embotellado ya servido) exige elegir, sin default:
//   · "Regresó íntegro"  → reversa: vuelve al inventario (queda rastro por cajero).
//   · "Se abrió o se dañó" → reversa + merma: pérdida VISIBLE en el reporte de mermas.
// El sistema NO puede saber si la botella se abrió: SOLO la persona → se fuerza elegir.
// Los platillos ya salidos generan su par reversa/merma automático en el backend; los no
// entregados no mueven inventario. Aquí solo se decide lo que el sistema no puede inferir.
// ===========================================================================
function CancelOrderModal({ order, onClose, onCancelled }:
  { order: Order; onClose: () => void; onCancelled: (o: Order) => void }) {
  // Grupos de producto ENTREGADO (readyAt) que controla inventario → exigen decisión.
  const decisionGroups = groupOrderItems(order.items)
    .filter((g) => g.rep.readyAt && g.rep.tracksInventory);
  const [choice, setChoice] = useState<Record<string, 'intact' | 'damaged'>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allDecided = decisionGroups.every((g) => choice[g.rep.id]);

  const submit = async () => {
    setErr(null);
    if (!allDecided) { setErr('Elige qué pasó con cada producto ya entregado.'); return; }
    // Una entrada de retorno por CADA order_item del grupo (el backend clavea por itemId).
    const returns = decisionGroups.flatMap((g) => g.ids.map((id) => ({ itemId: id, disposition: choice[g.rep.id] })));
    setBusy(true);
    try {
      const cancelled = (await api(`/pos/orders/${order.id}/cancel`, { method: 'POST', body: { returns } })) as Order;
      onCancelled(cancelled);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo cancelar la comanda');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Cancelar comanda</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        {decisionGroups.length === 0 ? (
          <p className="mb-4 text-sm text-slate-600">
            ¿Cancelar esta comanda? Los platillos ya salidos se registran como merma (pérdida visible);
            lo que no se preparó no mueve inventario.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-600">
              Hay producto <b>ya entregado</b>. Indica qué pasó con cada uno (obligatorio):
            </p>
            <ul className="mb-4 space-y-3">
              {decisionGroups.map((g) => (
                <li key={g.rep.id} className="rounded-xl border border-slate-200 p-3">
                  <p className="mb-2 text-sm font-medium text-slate-800">
                    <span className="tabular-nums text-slate-400">{g.qty}×</span> {g.rep.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setChoice((c) => ({ ...c, [g.rep.id]: 'intact' }))}
                      className={`rounded-lg px-2 py-1.5 text-xs font-medium ring-1 transition ${
                        choice[g.rep.id] === 'intact' ? 'bg-emerald-600 text-white ring-emerald-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}>
                      Regresó íntegro
                    </button>
                    <button type="button" onClick={() => setChoice((c) => ({ ...c, [g.rep.id]: 'damaged' }))}
                      className={`rounded-lg px-2 py-1.5 text-xs font-medium ring-1 transition ${
                        choice[g.rep.id] === 'damaged' ? 'bg-rose-600 text-white ring-rose-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'}`}>
                      Se abrió o se dañó
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {err && <p className="mb-3 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            No cancelar
          </button>
          <button type="button" onClick={submit} disabled={busy || !allDecided}
            className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
            {busy ? 'Cancelando…' : 'Cancelar comanda'}
          </button>
        </div>
      </div>
    </div>
  );
}

// V146 — el fallback browser del ticket de cocina ahora es comandaHtml (58/80mm, en
// src/printing/ticket.ts), ruteado por printTicket(kind:'comanda'). kitchenFallbackHtml
// (V49) se retiró: el HTML térmico vive en un solo lugar y respeta el ancho de papel.

// ===========================================================================
// V86 — builder TOUCH-FIRST "Arma tu platillo": variante (si hay) + grupos como
// chips grandes (radio si required+single, multi si no), total corriente SIEMPRE
// visible, min/max guiados en la UI (el server re-valida) y confirmación en UN
// tap. Cero teclado en el camino de armado. En edición (editItem) la variante y
// la cantidad quedan fijas (el precio base es snapshot de la línea).
// ===========================================================================
function DishBuilderModal({
  product, busy, editItem, onClose, onConfirm,
}: {
  product: MenuItem;
  busy: boolean;
  editItem?: OrderItem;
  onClose: () => void;
  onConfirm: (sel: { variantId?: string; qty: number; modifierOptionIds: string[] }) => void;
}) {
  const editing = !!editItem;
  const variants = product.variants ?? [];
  const groups = product.modifierGroups ?? [];
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(
    editing
      ? (editItem!.modifiers ?? []).filter((m) => m.modifierOptionId).map((m) => m.modifierOptionId!)
      : [],
  ));

  const toggleOption = (g: ModGroup, o: ModOption) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const single = g.minSelect >= 1 && g.maxSelect === 1;
      const inGroup = g.options.filter((x) => next.has(x.id));
      if (next.has(o.id)) {
        next.delete(o.id); // radio también permite des-elegir (el botón guía el min)
      } else if (single) {
        for (const x of inGroup) next.delete(x.id); // radio: tap cambia la selección
        next.add(o.id);
      } else if (g.maxSelect == null || inGroup.length < g.maxSelect) {
        next.add(o.id);
      }
      return next;
    });
  };

  // Total corriente: base (variante o precio del producto) × qty + Σ deltas
  // (los deltas se pliegan UNA vez por línea, server-side — misma convención aquí).
  const basePrice = editing
    ? Number(editItem!.unitPrice) || 0
    : variants.length > 0
      ? Number(variants.find((v) => v.id === variantId)?.price ?? 0)
      : product.price;
  const deltas = groups.flatMap((g) => g.options).filter((o) => selected.has(o.id))
    .reduce((acc, o) => acc + o.priceDelta, 0);
  const effQty = editing ? Number(editItem!.qty) || 1 : qty;
  const total = basePrice * effQty + deltas;

  // Faltantes: variante (en alta) + mínimos por grupo. El botón lo dice ("Elige salsa").
  const missing: string[] = [];
  if (!editing && variants.length > 0 && !variantId) missing.push('tamaño');
  for (const g of groups) {
    const n = g.options.filter((o) => selected.has(o.id)).length;
    if (n < g.minSelect) missing.push(g.name.toLowerCase());
  }

  const confirm = () => onConfirm({
    ...(variantId ? { variantId } : {}),
    qty: effQty,
    modifierOptionIds: [...selected],
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}>
        {/* Header sticky: nombre + total corriente SIEMPRE visible. */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="font-serif text-xl text-slate-900">{product.name}</h3>
            {/* V126 (#310) — paquete prix-fixe: menú por tiempos a precio fijo. */}
            <p className="text-[11px] uppercase tracking-wide text-slate-400">
              {product.isPrixFixe ? 'Paquete · menú por tiempos' : editing ? 'Re-armar platillo' : 'Arma tu platillo'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xl font-semibold tabular-nums text-slate-900">{mxn(total)}</span>
            <button onClick={onClose} className="grid h-11 w-11 place-items-center rounded-xl text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Variante como PRIMERA sección (misma gramática de chips). */}
          {!editing && variants.length > 0 && (
            <section className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tamaño <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Elige 1</span>
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {variants.map((v) => {
                  const on = variantId === v.id;
                  // PR2 — pildora de disponibilidad por talla (Sum kardex por variante, PR1).
                  const av = v.available;
                  const out = av != null && av <= 0;
                  return (
                    <button key={v.id} onClick={() => setVariantId(v.id)}
                      className={`flex min-h-14 flex-col items-start justify-center rounded-xl border px-4 py-2 text-left transition ${on ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-800 active:bg-slate-100'}`}>
                      <span className="text-base font-medium">{v.name}</span>
                      <span className={`flex items-center gap-1.5 text-xs tabular-nums ${on ? 'text-slate-300' : 'text-slate-500'}`}>
                        {mxn(v.price)}
                        {av != null && (
                          <span className={`rounded px-1 text-[10px] font-semibold ${out ? 'bg-rose-100 text-rose-700' : av <= 3 ? 'bg-amber-100 text-amber-700' : (on ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600')}`}>
                            {out ? 'agotada' : `${av} disp`}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {groups.map((g) => {
            const nSel = g.options.filter((o) => selected.has(o.id)).length;
            const single = g.minSelect >= 1 && g.maxSelect === 1;
            const maxed = g.maxSelect != null && nSel >= g.maxSelect;
            const badge = single
              ? 'Elige 1'
              : g.minSelect >= 1
                ? `Elige ${g.minSelect}${g.maxSelect ? `–${g.maxSelect}` : '+'}`
                : g.maxSelect ? `Hasta ${g.maxSelect}` : 'Opcional';
            const ok = nSel >= g.minSelect;
            return (
              <section key={g.id} className="mb-5">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {g.name}
                  <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{badge}</span>
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {g.options.map((o) => {
                    const on = selected.has(o.id);
                    const blocked = !on && !single && maxed;
                    return (
                      <button key={o.id} disabled={blocked} onClick={() => toggleOption(g, o)}
                        className={`flex min-h-14 flex-col items-start justify-center rounded-xl border px-4 py-2 text-left transition ${
                          on ? 'border-slate-900 bg-slate-900 text-white'
                            : blocked ? 'border-slate-100 bg-slate-50 text-slate-300'
                              : 'border-slate-200 bg-white text-slate-800 active:bg-slate-100'
                        }`}>
                        <span className="text-base font-medium">{o.name}</span>
                        {o.priceDelta > 0 && (
                          <span className={`text-xs tabular-nums ${on ? 'text-slate-300' : 'text-slate-500'}`}>+{mxn(o.priceDelta)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {/* Cantidad: stepper grande, sin teclado. En edición no se toca (PATCH no mueve qty). */}
          {!editing && (
            <section className="mb-2 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">Cantidad</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty((n) => Math.max(1, n - 1))}
                  className="grid h-12 w-12 place-items-center rounded-xl bg-white text-slate-700 ring-1 ring-slate-200 active:bg-slate-100"><Minus className="h-5 w-5" /></button>
                <span className="w-8 text-center text-lg font-semibold tabular-nums">{qty}</span>
                <button onClick={() => setQty((n) => n + 1)}
                  className="grid h-12 w-12 place-items-center rounded-xl bg-white text-slate-700 ring-1 ring-slate-200 active:bg-slate-100"><Plus className="h-5 w-5" /></button>
              </div>
            </section>
          )}
        </div>

        {/* Confirmación en UN tap: el total va EN el botón; deshabilitado hasta cubrir mínimos. */}
        <div className="border-t border-slate-100 p-4">
          <button disabled={busy || missing.length > 0} onClick={confirm}
            className="w-full rounded-xl bg-[var(--brand-primary)] px-4 py-4 text-base font-semibold text-[var(--brand-primary-fg)] active:bg-[var(--brand-primary-hover)] disabled:opacity-40">
            {missing.length > 0
              ? `Elige ${missing.join(', ')}`
              : busy ? 'Agregando…' : editing ? `Guardar — ${mxn(total)}` : `Agregar a comanda — ${mxn(total)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// V48 — nota + extras de un ítem (lectura): se muestra bajo la línea, en comanda y ticket.
// V86 — los modificadores de catálogo se prefijan con su grupo ("Salsa: Verde");
// el precio solo se muestra cuando suma (> 0).
// ===========================================================================
function ItemMods({ item }: { item: OrderItem }) {
  const mods = item.modifiers ?? [];
  if (!item.notes && mods.length === 0) return null;
  return (
    <div className="mt-0.5 pl-4 text-[11px] leading-snug text-slate-500">
      {item.notes && <p className="italic">“{item.notes}”</p>}
      {mods.map((m, i) => (
        <p key={m.id ?? i} className="flex items-center justify-between">
          <span>{m.groupName ? `${m.groupName}: ${m.name}` : `+ ${m.name}`}</span>
          {Number(m.price) > 0 && <span className="tabular-nums">{mxn(m.price)}</span>}
        </p>
      ))}
    </div>
  );
}

// ===========================================================================
// V48 — editor de nota + extras con precio de un ítem de la comanda.
// ===========================================================================
function ItemModsModal({
  item, busy, onClose, onSave,
}: {
  item: OrderItem;
  busy: boolean;
  onClose: () => void;
  onSave: (itemId: string, notes: string, modifiers: ItemModifier[]) => void;
}) {
  const [notes, setNotes] = useState(item.notes ?? '');
  // V86 — este editor toca SOLO los extras AD-HOC (modifier_option_id NULL); los
  // modificadores de catálogo se re-arman con el builder (botón de capas) y el
  // PATCH es replace PARCIAL POR TIPO — así no se pisan entre sí.
  const [mods, setMods] = useState<ItemModifier[]>(
    (item.modifiers ?? []).filter((m) => !m.modifierOptionId)
      .map((m) => ({ name: m.name, price: Number(m.price) || 0 })),
  );
  const catalogCount = (item.modifiers ?? []).filter((m) => m.modifierOptionId).length;

  const setMod = (i: number, patch: Partial<ItemModifier>) =>
    setMods((xs) => xs.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addRow = () => setMods((xs) => [...xs, { name: '', price: 0 }]);
  const removeRow = (i: number) => setMods((xs) => xs.filter((_, j) => j !== i));

  const submit = () => {
    // Descarta extras sin nombre; el backend revalida y recalcula el total.
    const clean = mods
      .map((m) => ({ name: m.name.trim(), price: Number(m.price) || 0 }))
      .filter((m) => m.name.length > 0);
    onSave(item.id, notes.trim(), clean);
  };

  const extrasTotal = mods.reduce((acc, m) => acc + (Number(m.price) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Nota y extras</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-4 text-xs text-slate-500">{Number(item.qty)}× {item.description}</p>
        {catalogCount > 0 && (
          <p className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
            El armado del platillo ({catalogCount} {catalogCount === 1 ? 'modificador' : 'modificadores'}) se
            edita con el botón <Layers className="inline h-3 w-3" /> — aquí solo nota y extras libres.
          </p>
        )}

        <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Nota (modificación)</p>
        <input className="input mb-4" placeholder="Ej. sin cebolla, término medio…"
          value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="mb-1 flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-slate-400">Extras (suman al total)</p>
          <button onClick={addRow} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900">
            <Plus className="h-3.5 w-3.5" /> Agregar
          </button>
        </div>
        {mods.length === 0 ? (
          <p className="mb-3 text-xs text-slate-400">Sin extras. Agrega "queso", "doble carne", etc.</p>
        ) : (
          <div className="mb-3 space-y-2">
            {mods.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                {/* Nombre = ANCHO (flex-1 → flex-basis 0, crece); monto = ANGOSTO fijo ~5rem.
                    OJO (por qué fallaba 2 veces): `.input { width:100% }` es CSS sin @layer
                    DESPUÉS de @tailwind utilities → gana a `w-20`, así que el monto heredaba
                    flex-basis:auto = 100% y con shrink-0 acaparaba toda la fila, colapsando el
                    nombre. Se fija el flex del monto EXPLÍCITO (grow-0 shrink-0 basis-20) para
                    que la anchura la mande el flex-basis (5rem), no el width:100%. */}
                <input className="input min-w-0 flex-1" placeholder="Nombre del extra"
                  value={m.name} onChange={(e) => setMod(i, { name: e.target.value })} />
                <input className="input grow-0 shrink-0 basis-20 text-right" type="number" min="0" step="0.01" placeholder="$"
                  value={m.price} onChange={(e) => setMod(i, { price: Number(e.target.value) })} />
                <button onClick={() => removeRow(i)} className="shrink-0 text-slate-300 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mb-4 rounded-xl bg-slate-50 p-3">
          <Row label="Extras" value={mxn(extrasTotal)} muted />
        </div>

        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Cobro: método + propina → registra el pago (no lo procesa)
// ===========================================================================
const METHODS: { id: 'CASH' | 'CARD' | 'TRANSFER'; label: string; Icon: any }[] = [
  { id: 'CASH', label: 'Efectivo', Icon: Banknote },
  { id: 'CARD', label: 'Tarjeta', Icon: CreditCard },
  { id: 'TRANSFER', label: 'Transferencia', Icon: ArrowLeftRight },
];
const TIP_PCTS = [0, 10, 15, 20];

function CobroModal({ order, onClose, onPaid, onPartial, sourceModule = 'gastro' }:
  { order: Order; onClose: () => void; onPaid: (o: Order) => void;
    /** PINK #2 — un cobro PARCIAL deja la orden OPEN con saldo: el parent refresca sin ir al ticket. */
    onPartial?: (o: Order) => void;
    sourceModule?: 'gastro' | 'boutique' | 'gym' }) {
  const { user } = useAuth();
  const roundCash = user?.tenant?.cashRoundingEnabled ?? false;
  // PINK #2 (pieza 3a) — el cobro SIMPLE es el default (90% de las mesas); "Separado o mixto"
  // abre la calculadora por renglones (SplitCobro). El simple NO cambia en nada.
  const [split, setSplit] = useState(false);
  const [method, setMethod] = useState<'CASH' | 'CARD' | 'TRANSFER'>('CASH');
  const [tip, setTip] = useState('0');
  const [received, setReceived] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // §32/A0 — cobrar como PEDIDO PARA RECOGER (entrega pendiente). Solo gastro.
  const [pickup, setPickup] = useState(false);
  const [pickupName, setPickupName] = useState('');

  const tipNum = Number(tip) || 0;
  const grandTotal = (Number(order.total) || 0) + tipNum;

  // §30 — cambio en efectivo. `received` vacío = no se captura (comportamiento
  // previo). Con monto: cambio en vivo + validación recibido >= total.
  const isCash = method === 'CASH';
  const hasReceived = isCash && received.trim() !== '';
  const ch = computeChange(Number(received) || 0, grandTotal, roundCash);
  const insufficient = hasReceived && !ch.sufficient;
  // Botones rápidos: $200, $500, exacto, y el siguiente redondo por encima del total.
  const quickBills = Array.from(new Set([200, 500, nextRoundAbove(grandTotal)]))
    .filter((b) => b >= grandTotal).sort((a, b) => a - b);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const paid = (await api(`/pos/orders/${order.id}/close`, {
        method: 'POST',
        body: {
          method, tip: tipNum,
          ...(hasReceived ? { cashReceived: Number(received) } : {}),
          ...(pickup ? { pickup: true, ...(pickupName.trim() ? { customerLabel: pickupName.trim() } : {}) } : {}),
        },
      })) as Order;
      onPaid(paid);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo registrar el cobro');
      setBusy(false);
    }
  };

  // PINK #2 — camino "Separado o mixto": la calculadora por renglones toma el control del modal.
  if (split) {
    return (
      <SplitCobro order={order} sourceModule={sourceModule} roundCash={roundCash}
        onClose={onClose} onPaid={onPaid} onPartial={onPartial}
        onBackToSimple={() => setSplit(false)} />
    );
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      {/* responsive-turno: max-h + overflow — el cobro en efectivo abre el teclado
          para "Recibido" y el botón Cobrar vive hasta abajo; sin scroll interno
          quedaba tapado en teléfono. */}
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Cobrar</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        {/* PINK #2 — abre el cobro separado/mixto (varias formas de pago / dividir la cuenta). */}
        <button onClick={() => setSplit(true)}
          className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100">
          <Layers className="h-4 w-4" /> Separado o mixto (varias formas o dividir)
        </button>

        <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Método de pago</p>
        {/* responsive-turno: 2-up en teléfono para que "Transferencia" no se apriete;
            3-up desde sm:. */}
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {METHODS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setMethod(id)}
              className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-xs font-medium ${
                method === id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Propina</p>
        <div className="mb-2 flex gap-2">
          {TIP_PCTS.map((pct) => (
            <button key={pct} onClick={() => setTip(((Number(order.total) || 0) * pct / 100).toFixed(2))}
              className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
              {pct === 0 ? 'Sin' : `${pct}%`}
            </button>
          ))}
        </div>
        <input className="input mb-4" type="number" min="0" step="0.01" value={tip}
          onChange={(e) => setTip(e.target.value)} placeholder="Propina ($)" />

        <div className="mb-4 rounded-xl bg-slate-50 p-3 text-sm">
          <Row label="Cuenta" value={mxn(order.total)} muted />
          <Row label="Propina" value={mxn(tipNum)} muted />
          <Row label="Total a cobrar" value={mxn(grandTotal)} strong />
        </div>

        {/* §30 — cobro en efectivo: recibido + cambio en vivo (grande) + billetes rápidos. */}
        {isCash && (
          <div className="mb-4">
            <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Recibido (efectivo)</p>
            <input className="input mb-2 text-lg tabular-nums" type="number" min="0" step="0.01" inputMode="decimal"
              value={received} onChange={(e) => setReceived(e.target.value)} placeholder="Monto entregado por el cliente" />
            <div className="mb-3 flex flex-wrap gap-2">
              <button onClick={() => setReceived(grandTotal.toFixed(2))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Monto exacto</button>
              {quickBills.map((b) => (
                <button key={b} onClick={() => setReceived(b.toFixed(2))}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">{mxn(b)}</button>
              ))}
            </div>
            {hasReceived && ch.sufficient && (
              <div className="rounded-2xl bg-emerald-50 p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-emerald-700">Cambio</p>
                <p className="text-4xl font-bold tabular-nums text-emerald-800">{mxn(ch.change)}</p>
                {ch.diff > 0 && (
                  <p className="mt-1 text-[11px] text-emerald-600">Redondeado a favor del cliente — el negocio absorbe {mxn(ch.diff)}</p>
                )}
              </div>
            )}
            {insufficient && (
              <p className="alert-over rounded-xl px-3 py-2 text-xs">El monto recibido es menor al total a cobrar.</p>
            )}
          </div>
        )}

        {/* §32/A0 — cobrar como PEDIDO PARA RECOGER: entrega pendiente (mostrador). Solo
            gastro. El inventario baja al preparar/entregar, no ahora. OCULTO si la orden
            YA ES un pedido para recoger (Pendientes): marcarlo re-generaría el código. */}
        {sourceModule === 'gastro' && order.fulfillmentStatus == null && (
          <div className="mb-3 rounded-xl border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={pickup} onChange={(e) => setPickup(e.target.checked)} />
              Para recoger (entrega pendiente)
            </label>
            {pickup && (
              <>
                <p className="mt-1 text-[11px] text-slate-500">Se genera un código de entrega; el pedido queda pendiente de preparar. Se llama al cliente por código o nombre.</p>
                <input className="input mt-2" value={pickupName} onChange={(e) => setPickupName(e.target.value)}
                  placeholder="Nombre del cliente — ej. Ana López (opcional)" maxLength={120} />
              </>
            )}
          </div>
        )}

        {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || insufficient}>
            {busy ? 'Registrando…' : pickup ? 'Cobrar y preparar' : 'Registrar cobro'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// PINK #2 (pieza 3a) — COBRO SEPARADO / MIXTO (calculadora por renglones)
// ===========================================================================
// Consume POST /pos/orders/:id/pay (fn_pos_charge_record). Cada renglón es UN pago
// (método + monto + propina $/% + propina-en-efectivo). Un cobro PARCIAL deja la orden
// OPEN con saldo. El excedente de redondeo (≤ max(10% del total, $50)) lo absorbe el
// servidor como propina del último renglón; un excedente grande se rechaza. Cero window.alert.
type PayMethod = 'CASH' | 'CARD' | 'CARD_CREDIT' | 'CARD_DEBIT' | 'TRANSFER';
const SPLIT_METHODS: { id: PayMethod; label: string; Icon: any }[] = [
  { id: 'CASH', label: 'Efectivo', Icon: Banknote },
  { id: 'CARD_CREDIT', label: 'Crédito', Icon: CreditCard },
  { id: 'CARD_DEBIT', label: 'Débito', Icon: CreditCard },
  { id: 'TRANSFER', label: 'Transfer.', Icon: ArrowLeftRight },
];
interface SplitLine {
  key: string;
  method: PayMethod;
  amount: string;                 // consumo (sin propina), pesos
  tipMode: 'amount' | 'pct';
  tip: string;                    // monto o % según tipMode
  tipInCash: boolean;
  cashReceived: string;           // §30, solo CASH
  // por platillo (3b): si presente, este renglón paga estos order_items (monto BLOQUEADO a su suma).
  targets?: { orderItemId: string; amount: number }[];
  targetLabel?: string;
}
const cents = (s: string | number) => Math.round((Number(s) || 0) * 100);
let splitKeySeq = 0;
const freshLine = (amount: number, method: PayMethod = 'CASH'): SplitLine => ({
  key: `l${++splitKeySeq}`, method, amount: amount > 0 ? amount.toFixed(2) : '',
  tipMode: 'amount', tip: '', tipInCash: false, cashReceived: '',
});

function SplitCobro({ order, sourceModule, roundCash, onClose, onPaid, onPartial, onBackToSimple }: {
  order: Order; sourceModule: 'gastro' | 'boutique' | 'gym'; roundCash: boolean;
  onClose: () => void; onPaid: (o: Order) => void; onPartial?: (o: Order) => void;
  onBackToSimple: () => void;
}) {
  const totalCents = cents(order.total);
  // Saldo ya cobrado (parcial previo): el dual-write llena order.payments; Σ amount = consumo pagado.
  const paidBeforeCents = (order.payments || []).reduce((s, p) => s + cents(p.amount), 0);
  const remainingBeforeCents = Math.max(0, totalCents - paidBeforeCents);

  const [lines, setLines] = useState<SplitLine[]>(() => [freshLine(remainingBeforeCents / 100)]);
  const [splitN, setSplitN] = useState('2');
  const [cardAmt, setCardAmt] = useState('');
  const [cardKind, setCardKind] = useState<'CARD_CREDIT' | 'CARD_DEBIT'>('CARD_CREDIT');
  const [pickup, setPickup] = useState(false);
  const [pickupName, setPickupName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const patch = (key: string, p: Partial<SplitLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  const remove = (key: string) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  const lineTipCents = (l: SplitLine) =>
    l.tipMode === 'pct' ? Math.round(cents(l.amount) * (Number(l.tip) || 0) / 100) : cents(l.tip);

  const allocatedCents = lines.reduce((s, l) => s + cents(l.amount), 0);
  const remainingAfterCents = remainingBeforeCents - allocatedCents;
  const excessCents = Math.max(0, -remainingAfterCents);
  // Espejo EXACTO del umbral del servidor (V188c): max(10% del total, $50).
  const excessThreshold = Math.max(Math.round(totalCents * 0.10), 5000);
  const excessTooBig = excessCents > excessThreshold;
  const tipsCents = lines.reduce((s, l) => s + lineTipCents(l), 0);

  // Efectivo insuficiente (guía; el servidor es la verdad).
  const cashShort = lines.some((l) =>
    l.method === 'CASH' && l.cashReceived.trim() !== '' &&
    cents(l.cashReceived) < cents(l.amount) + lineTipCents(l));
  const anyZero = lines.some((l) => cents(l.amount) <= 0);
  const canSubmit = lines.length > 0 && !anyZero && !excessTooBig && !cashShort;

  // Helper — DIVIDIR ENTRE N: N renglones iguales sobre el saldo. Redondeo hacia arriba al
  // centavo → Σ puede pasar el saldo por centavos → el servidor los absorbe como propina.
  const divideBy = () => {
    const n = Math.max(1, Math.floor(Number(splitN) || 0));
    if (n < 1 || remainingBeforeCents <= 0) return;
    const per = Math.ceil(remainingBeforeCents / n);
    setLines(Array.from({ length: n }, () => freshLine(per / 100)));
  };
  // Helper — MONTO EN TARJETA: un renglón con tarjeta por el monto capturado + el resto en efectivo.
  const cardThenCash = () => {
    const card = Math.min(cents(cardAmt), remainingBeforeCents);
    if (card <= 0) return;
    const rest = remainingBeforeCents - card;
    const ls = [freshLine(card / 100, cardKind)];
    if (rest > 0) ls.push(freshLine(rest / 100, 'CASH'));
    setLines(ls);
  };
  // Helper — DIVIDIR POR PLATILLO: un renglón por ítem, monto BLOQUEADO al total del platillo,
  // con su target (order_item). El mesero elige método/propina de cada uno.
  const perDish = () => {
    const its = (order.items || []).filter((it) => cents(it.lineTotal) > 0);
    if (!its.length) return;
    setLines(its.map((it) => ({
      ...freshLine(Number(it.lineTotal)),
      targets: [{ orderItemId: it.id, amount: Number(Number(it.lineTotal).toFixed(2)) }],
      targetLabel: `${it.qty}× ${it.description}`,
    })));
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        lines: lines.map((l) => {
          const base: Record<string, unknown> = { method: l.method, amount: Number(l.amount) || 0 };
          if (l.tipMode === 'pct') { if (Number(l.tip) > 0) base.tipPct = Number(l.tip); }
          else if (Number(l.tip) > 0) base.tip = Number(l.tip);
          if (l.tipInCash) base.tipInCash = true;
          if (l.method === 'CASH' && l.cashReceived.trim() !== '') base.cashReceived = Number(l.cashReceived);
          if (l.targets?.length) base.targets = l.targets.map((t) => ({ orderItemId: t.orderItemId, amount: t.amount }));
          return base;
        }),
        ...(pickup ? { pickup: true, ...(pickupName.trim() ? { customerLabel: pickupName.trim() } : {}) } : {}),
      };
      const res = (await api(`/pos/orders/${order.id}/pay`, { method: 'POST', body: payload })) as Order;
      // CLOSED = saldada → ticket; OPEN = parcial → refresca la comanda con su saldo (no ticket).
      if (res.status === 'CLOSED') onPaid(res);
      else if (onPartial) onPartial(res);
      else onPaid(res);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo registrar el cobro');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Cobro separado / mixto</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <button onClick={onBackToSimple}
          className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" /> Volver a cobro simple
        </button>

        {/* Saldo — siempre visible. */}
        <div className="mb-4 rounded-xl bg-slate-50 p-3 text-sm">
          <Row label="Cuenta" value={mxn(order.total)} muted />
          {paidBeforeCents > 0 && <Row label="Ya cobrado" value={mxn(paidBeforeCents / 100)} muted />}
          <Row label="Por cobrar" value={mxn(remainingBeforeCents / 100)} muted />
          <Row label="Capturado" value={mxn(allocatedCents / 100)} muted />
          {tipsCents > 0 && <Row label="Propinas" value={mxn(tipsCents / 100)} muted />}
          <Row
            label={remainingAfterCents > 0 ? 'Falta' : excessCents > 0 ? 'Excedente (→ propina)' : 'Saldo'}
            value={mxn(Math.abs(remainingAfterCents) / 100)} strong />
        </div>

        {/* Ayudas rápidas. */}
        <div className="mb-4 space-y-2 rounded-xl border border-slate-200 p-3">
          {/* Aclaración: estas ayudas NO son un pago aparte — arman/llenan los renglones de abajo. */}
          <p className="text-[11px] text-slate-400">Atajos: llenan los pagos de abajo automáticamente. Ajústalos si hace falta.</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Dividir entre</span>
            <input className="input w-16 py-1 text-center" type="number" min="1" step="1" value={splitN}
              onChange={(e) => setSplitN(e.target.value)} />
            <button onClick={divideBy} className={btnGhost}>Partes iguales</button>
            <button onClick={perDish} className={btnGhost}>Por platillo</button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-400">Monto en tarjeta</span>
            <input className="input w-24 py-1" type="number" min="0" step="0.01" value={cardAmt}
              onChange={(e) => setCardAmt(e.target.value)} placeholder="$" />
            <select className="input w-auto py-1 text-xs" value={cardKind}
              onChange={(e) => setCardKind(e.target.value as 'CARD_CREDIT' | 'CARD_DEBIT')}>
              <option value="CARD_CREDIT">Crédito</option>
              <option value="CARD_DEBIT">Débito</option>
            </select>
            <button onClick={cardThenCash} className={btnGhost}>+ resto efectivo</button>
          </div>
          <p className="text-[11px] text-slate-400">
            "Monto en tarjeta" es un atajo: pon cuánto va con tarjeta y el resto queda en efectivo —
            crea/rellena los dos pagos de abajo. No es una captura extra.
          </p>
        </div>

        {/* Renglones de pago. */}
        <div className="space-y-3">
          {lines.map((l, i) => {
            const short = l.method === 'CASH' && l.cashReceived.trim() !== '' &&
              cents(l.cashReceived) < cents(l.amount) + lineTipCents(l);
            const ch = l.method === 'CASH' && l.cashReceived.trim() !== ''
              ? computeChange(Number(l.cashReceived) || 0, (Number(l.amount) || 0) + lineTipCents(l) / 100, roundCash)
              : null;
            return (
              <div key={l.key} className="rounded-xl border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">
                    Pago {i + 1}{l.targetLabel ? ` · ${l.targetLabel}` : ''}
                  </span>
                  {lines.length > 1 && (
                    <button onClick={() => remove(l.key)} className="text-slate-300 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mb-2 grid grid-cols-4 gap-1.5">
                  {SPLIT_METHODS.map(({ id, label, Icon }) => (
                    <button key={id} onClick={() => patch(l.key, { method: id })}
                      className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-[11px] font-medium ${
                        l.method === id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] text-slate-400">Monto</span>
                    <input className="input" type="number" min="0" step="0.01" value={l.amount}
                      readOnly={!!l.targets} disabled={!!l.targets}
                      onChange={(e) => patch(l.key, { amount: e.target.value })} />
                  </label>
                  <label className="w-28">
                    <span className="mb-1 block text-[11px] text-slate-400">Propina</span>
                    <div className="flex">
                      <input className="input rounded-r-none" type="number" min="0" step="0.01" value={l.tip}
                        onChange={(e) => patch(l.key, { tip: e.target.value })} placeholder="0" />
                      <button
                        onClick={() => patch(l.key, { tipMode: l.tipMode === 'pct' ? 'amount' : 'pct', tip: '' })}
                        className="rounded-l-none rounded-r-lg border border-l-0 border-slate-200 px-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        {l.tipMode === 'pct' ? '%' : '$'}
                      </button>
                    </div>
                  </label>
                </div>
                <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600">
                  <input type="checkbox" checked={l.tipInCash}
                    onChange={(e) => patch(l.key, { tipInCash: e.target.checked })} />
                  Propina en efectivo (la recibe el mesero)
                </label>
                {l.method === 'CASH' && (
                  <div className="mt-2">
                    <input className="input tabular-nums" type="number" min="0" step="0.01" inputMode="decimal"
                      value={l.cashReceived} onChange={(e) => patch(l.key, { cashReceived: e.target.value })}
                      placeholder="Recibido (efectivo) — opcional" />
                    {ch?.sufficient && (
                      <p className="mt-1 text-[11px] text-emerald-700">Cambio {mxn(ch.change)}
                        {ch.diff > 0 && ` · el negocio absorbe ${mxn(ch.diff)}`}</p>
                    )}
                    {short && <p className="alert-over mt-1 rounded-lg px-2 py-1 text-[11px]">Recibido menor al total del renglón.</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button onClick={() => setLines((ls) => [...ls, freshLine(Math.max(0, remainingAfterCents) / 100)])}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50">
          <Plus className="h-4 w-4" /> Agregar pago
        </button>

        {excessTooBig && (
          <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">
            El cobro excede el saldo por {mxn(excessCents / 100)}. Si es propina, captúrala en el campo de propina.
          </p>
        )}
        {remainingAfterCents > 0 && !excessTooBig && (
          <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
            Cobro parcial: la orden queda ABIERTA con un saldo de {mxn(remainingAfterCents / 100)}.
          </p>
        )}

        {sourceModule === 'gastro' && order.fulfillmentStatus == null && (
          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={pickup} onChange={(e) => setPickup(e.target.checked)} />
              Para recoger (entrega pendiente)
            </label>
            {pickup && (
              <input className="input mt-2" value={pickupName} onChange={(e) => setPickupName(e.target.value)}
                placeholder="Nombre del cliente — ej. Ana López (opcional)" maxLength={120} />
            )}
          </div>
        )}

        {err && <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || !canSubmit}>
            {busy ? 'Registrando…' : remainingAfterCents > 0 ? 'Cobrar parcial' : 'Registrar cobro'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// PINK #2 (pieza 4.2) — PASAR A CUENTA APARTE (mover renglones a una orden nueva)
// ===========================================================================
// Elige N renglones y muévelos a una cuenta NUEVA de la MISMA mesa (cada cuenta cobra y
// factura por separado: una orden = un CFDI). Debe quedar al menos un renglón en la original.
// Los ya pagados no se pueden mover (el servidor lo rechaza y el error sale en banner inline).
function SplitAccountModal({ order, onClose, onSplit }: {
  order: Order; onClose: () => void; onSplit: (source: Order) => void;
}) {
  const groups = groupOrderItems(order.items || []);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (i: number) => setSel((s) => {
    const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n;
  });
  const chosen = groups.filter((_, i) => sel.has(i));
  const movingIds = chosen.flatMap((g) => g.ids);
  const movingTotal = chosen.reduce((s, g) => s + g.lineTotal, 0);
  const allSelected = movingIds.length >= (order.items?.length || 0);
  const canSubmit = movingIds.length > 0 && !allSelected;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const res = (await api(`/pos/orders/${order.id}/split-account`, {
        method: 'POST',
        body: { itemIds: movingIds, ...(label.trim() ? { customerLabel: label.trim() } : {}) },
      })) as { source: Order; moved: Order };
      onSplit(res.source);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo separar la cuenta');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Pasar a cuenta aparte</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Elige los renglones que van a la cuenta nueva. Se quedan en la misma mesa y se cobran
          y facturan por separado. Deja al menos un renglón en esta cuenta.
        </p>
        <div className="space-y-2">
          {groups.map((g, i) => (
            <label key={i} className={`flex cursor-pointer items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
              sel.has(i) ? 'border-sky-400 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'
            }`}>
              <span className="flex items-center gap-2 min-w-0">
                <input type="checkbox" checked={sel.has(i)} onChange={() => toggle(i)} />
                <span className="truncate text-sm text-slate-800">{g.qty}× {g.rep.description}</span>
              </span>
              <span className="shrink-0 tabular-nums text-sm text-slate-600">{mxn(g.lineTotal)}</span>
            </label>
          ))}
        </div>

        <div className="mt-3">
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="Etiqueta de la cuenta nueva — ej. Mesa 4 · Juan (opcional)" maxLength={120} />
        </div>

        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm">
          <Row label="A la cuenta nueva" value={mxn(movingTotal)} muted />
          <Row label="Se queda en esta" value={mxn((Number(order.total) || 0) - movingTotal)} strong />
        </div>

        {allSelected && movingIds.length > 0 && (
          <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
            No puedes mover todos los renglones: deja al menos uno en esta cuenta.
          </p>
        )}
        {err && <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose} disabled={busy}>Cancelar</button>
          <button className={btnPrimary} onClick={submit} disabled={busy || !canSubmit}>
            {busy ? 'Separando…' : 'Crear cuenta aparte'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Ticket / recibo (branded)
// ===========================================================================
/**
 * V117 — asignar/mover/quitar MESA a la orden abierta + comensales (opcional).
 * La conversión venta-rápida→mesa NO re-captura: el backend re-apunta table_id de la
 * MISMA orden (ítems/fires/pagos intactos). Solo mesas LIBRES de la sucursal de la
 * orden (el backend valida sucursal y rechaza mesas ocupadas con 409).
 */
function AssignTableModal({ order, onClose, onSaved }: {
  order: Order; onClose: () => void; onSaved: (o: Order) => void;
}) {
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableId, setTableId] = useState<string | null>(order.tableId);
  const [diners, setDiners] = useState<number>(order.diners ?? 0); // 0 = sin capturar
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setTables((await api('/pos/tables')) as Table[]); }
      catch { /* sin mesas: solo comensales */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      if (tableId !== order.tableId) body.tableId = tableId; // null explícito = quitar mesa
      if (diners >= 1 && diners !== (order.diners ?? 0)) body.diners = diners;
      const updated = Object.keys(body).length
        ? ((await api(`/pos/orders/${order.id}/table`, { method: 'PATCH', body })) as Order)
        : order;
      onSaved(updated);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo asignar la mesa.');
    } finally { setBusy(false); }
  };

  // Elegibles: libres + la mesa actual de la orden (para no "perderla" en la lista).
  const selectable = tables.filter((t) => t.status === 'FREE' || t.id === order.tableId);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Mesa y comensales</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        {loading ? <p className="text-sm text-slate-400">Cargando mesas…</p> : (
          <>
            {selectable.length > 0 ? (
              <div className="mb-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Mesa</p>
                <div className="grid grid-cols-3 gap-2">
                  {selectable.map((t) => (
                    <button key={t.id} onClick={() => setTableId(tableId === t.id ? null : t.id)}
                      className={`min-h-12 rounded-xl border px-2 text-sm font-medium transition ${tableId === t.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
                {order.tableId && tableId === null && (
                  <p className="mt-2 text-[11px] text-amber-700">Se quitará la mesa: la venta vuelve a «venta rápida».</p>
                )}
              </div>
            ) : (
              <p className="mb-4 text-sm text-slate-400">Sin mesas libres (o sin mesas configuradas). Puedes capturar solo los comensales.</p>
            )}

            <div className="mb-5">
              <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Comensales (opcional)</p>
              <Stepper label="Personas" value={diners} min={0} onChange={setDiners} />
            </div>

            {err && <p className="mb-3 text-sm text-rose-600">{err}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={busy}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={save} disabled={busy}
                className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {busy ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TicketView({ order, brandName, onClose, backLabel = 'Volver a mesas' }: { order: Order; brandName: string; onClose: () => void; backLabel?: string }) {
  const pay = order.payments?.[order.payments.length - 1];
  const tip = Number(pay?.tip) || 0;
  const grand = (Number(order.total) || 0) + tip;
  const methodLabel = METHODS.find((m) => m.id === pay?.method)?.label ?? pay?.method ?? '—';
  const [printMsg, setPrintMsg] = useState<string | null>(null);
  // 🅐 pieza 1 — una orden ABIERTA mostrada como ticket es la PRE-CUENTA: mismo formato
  // pero SIN QR de factura, SIN "Total cobrado" y SIN forma de pago.
  const isPreCuenta = order.status === 'OPEN';
  // V205 — ticket de PROMOCIÓN (cortesía de fidelidad, $0): sin cobro ni factura.
  const isPromo = order.isLoyaltyCourtesy === true;
  const loyaltyLine = order.loyalty?.stamped && order.loyalty.ticketLine
    ? { line: order.loyalty.ticketLine, rewardReady: order.loyalty.rewardReady } : null;
  // Fix 11/09 — CFDI activo (proxy: el tenant muestra el QR de factura en el
  // pagado) para pintar "solicita tu factura al pagar" en la pre-cuenta (sin QR).
  const [offersCfdiPreview, setOffersCfdiPreview] = useState(false);
  useEffect(() => {
    if (!isPreCuenta) return;
    let alive = true;
    fetchTicketSettings(null, true)
      .then((s) => { if (alive) setOffersCfdiPreview(!!s?.showInvoiceQr); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isPreCuenta]);

  // V146 — imprime el recibo por el ROL "caja": qz/red silencioso si hay impresora
  // configurada, o el navegador con formato térmico 58/80mm. Nunca en silencio.
  const doPrint = async () => {
    setPrintMsg(null);
    const items = groupOrderItems(order.items).map((g) => ({
      qty: g.qty, description: g.rep.description, lineTotal: g.lineTotal,
    }));
    const delivery = order.fulfillmentStatus && order.deliveryCode
      ? { code: order.deliveryCode, ref: order.customerLabel, statusLabel: fulfillLabel(order.fulfillmentStatus) }
      : null;
    const cash = pay?.method === 'CASH' && pay?.cashReceived != null
      ? { received: Number(pay.cashReceived), change: (Number(pay.cashReceived) - grand) + (Number(pay.roundingDiff) || 0) }
      : null;

    // V147 — personalización (logo mono, textos, QR configurable) del tenant/sucursal.
    const settings = await fetchTicketSettings(null, true).catch(() => null);
    const branding = settings ? await resolveBranding(settings) : null;
    // 🅐 pieza 1 — la pre-cuenta NUNCA lleva QR de autofactura (no hay pago que facturar).
    const wantInvoice = !isPreCuenta && !isPromo && (!settings || settings.showInvoiceQr);

    // Autofactura: mismo dato que SelfInvoiceTicketBlock — QR + código + emisor + plazo.
    // Va en AMBOS caminos (QR nativo en ESC/POS, imagen en HTML). Best-effort: si el
    // emisor no está listo, falla, o el tenant apagó el toggle, sale sin el bloque.
    let invEsc: any = null; let invHtml: any = null;
    try {
      const info: any = wantInvoice ? await api(`/cfdi-sales/ticket-info/${order.id}`) : null;
      if (info?.ready) {
        const fullUrl = `${window.location.origin}/factura/${info.code}`;
        const codeLbl = (String(info.code).match(/.{1,4}/g) ?? [info.code]).join('-').toUpperCase();
        const per = info.globalPeriodicity === '01' ? 'del día' : info.globalPeriodicity === '02' ? 'de la semana' : 'del mes';
        const globalNote = info.globalEnabled ? `Factura antes de que acabe el período ${per}.` : null;
        // V153 (Pink) — el aviso del plazo para autofacturar SIEMPRE se imprime, redactado
        // para sostenerse solo (antes quedaba suelto "hasta fin de mes").
        const windowLabel = info.windowDays
          ? `Puedes facturar esta compra en los próximos ${info.windowDays} días`
          : 'Puedes facturar esta compra hasta fin de mes';
        // V153 — la línea del emisor (razón social + RFC) es OPCIONAL por tenant
        // (ticket_settings.showIssuerLine, default ON). Se gatea aquí, en el ENSAMBLADO,
        // para que las TRES rutas (qz / ePOS-red / navegador) la respeten por construcción:
        // con showIssuer=false los renderers ya omiten la línea (issuer null) y el plazo queda.
        const showIssuer = !settings || settings.showIssuerLine !== false;
        const issuerRazon = showIssuer ? info.issuerRazon : null;
        const issuerRfc = showIssuer ? info.issuerRfc : null;
        const urlLabel = fullUrl.replace(/^https?:\/\//, '');
        invEsc = { code: codeLbl, qrUrl: fullUrl, urlLabel, issuerRazon, issuerRfc, windowLabel, globalNote };
        const qrDataUrl = await QRCode.toDataURL(fullUrl, { margin: 1, width: 240 });
        invHtml = { code: codeLbl, qrDataUrl, urlLabel, issuerRazon, issuerRfc, windowLabel, globalNote };
      }
    } catch { /* sin bloque de factura */ }

    const base = {
      brandName, delivery, items,
      subtotal: Number(order.subtotal) || 0, taxTotal: Number(order.taxTotal) || 0,
      total: Number(order.total) || 0, tip, grandTotal: grand, methodLabel, cash,
      preCuenta: isPreCuenta, // 🅐 pieza 1 — los renderers omiten cobrado/pago y cambian el título
      // Fix 11/09 — el tenant emite CFDI (proxy: muestra el QR de factura en el
      // pagado): en pre-cuenta se imprime "solicita tu factura al pagar" SIN QR.
      offersCfdi: !!settings?.showInvoiceQr,
      promo: isPromo, loyalty: loyaltyLine, // V205 — D2a: código + avance; ticket de promoción
    };
    const res = await printTicket({
      branchId: null, kind: 'ticket',
      escpos: (w) => salesTicket({ ...base, invoice: invEsc, branding: branding?.escFor(w) ?? null, qrModule: branding?.qrModule, cols: w === 58 ? 32 : 48 }),
      html: (w) => salesTicketHtml({ ...base, invoice: invHtml, branding: branding?.htmlFor(w) ?? null, qrSizeMm: branding?.qrSizeMm }, w),
    });
    if (res.usedFallback || res.method === 'browser') {
      setPrintMsg(res.error ? `⚠ ${res.error}` : 'Se abrió la vista de impresión del navegador.');
    }
  };

  return (
    <div>
      <button onClick={onClose} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> {backLabel}
      </button>
      <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 text-center">
          <p className="font-serif text-xl tracking-tight" style={{ color: 'var(--brand-primary, #0f172a)' }}>{brandName}</p>
          {isPreCuenta
            ? <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-600">Pre-cuenta · pendiente de pago</p>
            : isPromo
              ? <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-600">Ticket de promoción · cortesía de fidelidad</p>
              : <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Recibo de venta</p>}
        </div>
        {/* §32/A0 — pedido para recoger: el CÓDIGO GENERADO manda visualmente; la referencia
            (Hallazgo 3) va subordinada y ROTULADA "Ref:", nunca como número suelto. */}
        {order.fulfillmentStatus && order.deliveryCode && (
          <div className="mb-4 rounded-2xl bg-indigo-50 p-4 text-center ring-1 ring-indigo-200">
            <p className="text-[11px] uppercase tracking-wide text-indigo-700">Pedido para recoger — código</p>
            <p className="text-5xl font-bold tabular-nums tracking-widest text-indigo-800">{order.deliveryCode}</p>
            {order.customerLabel && (
              <p className="mt-1 text-sm text-indigo-700">Ref: <span className="font-medium">{order.customerLabel}</span></p>
            )}
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-500">{fulfillLabel(order.fulfillmentStatus)}</p>
          </div>
        )}
        <ul className="mb-3 divide-y divide-slate-100 border-y border-slate-100">
          {groupOrderItems(order.items).map((g) => (
            <li key={g.rep.id} className="py-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-700"><span className="tabular-nums text-slate-400">{g.qty}×</span> {g.rep.description}</span>
                <span className="tabular-nums text-slate-800">{mxn(g.lineTotal)}</span>
              </div>
              {/* V48 — nota y extras del ítem en el ticket de cobro. */}
              <ItemMods item={g.rep} />
            </li>
          ))}
        </ul>
        <Row label="Subtotal" value={mxn(order.subtotal)} muted />
        <Row label="IVA (16%)" value={mxn(order.taxTotal)} muted />
        <Row label="Total" value={mxn(order.total)} />
        {isPreCuenta ? (
          /* 🅐 pieza 1 — pre-cuenta: sin propina/cobrado/pago. Fix 11/09 — leyenda
             fiscal exacta; la línea de autofactura (sin QR) solo si el tenant emite CFDI. */
          <div className="mt-2 rounded-xl bg-violet-50 px-3 py-2 text-center ring-1 ring-violet-200">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">Cuenta — no es comprobante fiscal</p>
            {offersCfdiPreview && <p className="text-[11px] text-violet-600">Solicita tu factura al pagar</p>}
          </div>
        ) : isPromo ? (
          /* V205 — cortesía de fidelidad: documento separado, sin cobro (fuera del corte). */
          <div className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-center ring-1 ring-amber-200">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Cortesía — sin cobro</p>
          </div>
        ) : (<>
        <Row label="Propina" value={mxn(tip)} muted />
        <div className="mt-1 border-t border-slate-100 pt-1">
          <Row label="Total cobrado" value={mxn(grand)} strong />
        </div>
        {/* §30 — efectivo: recibido + cambio (redondeado si aplica) en el ticket. */}
        {pay?.method === 'CASH' && pay?.cashReceived != null && (
          <div className="mt-1 border-t border-slate-100 pt-1">
            <Row label="Recibido" value={mxn(pay.cashReceived)} muted />
            <Row label="Cambio" value={mxn((Number(pay.cashReceived) - grand) + (Number(pay.roundingDiff) || 0))} strong />
          </div>
        )}
        <p className="mt-3 text-center text-[11px] text-slate-400">Pago: {methodLabel} · ¡Gracias por tu visita!</p>
        {/* Fase 2 — autofactura: QR + código + emisor en el ticket (se
            auto-oculta si el emisor no está listo). Sale en window.print(). */}
        <SelfInvoiceTicketBlock orderId={order.id} />
        </>)}
        {/* V205 (D2a) — ticket sellado: código + avance, para que el cliente no pierda su cartilla. */}
        {loyaltyLine && !isPreCuenta && (
          <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-center ring-1 ring-emerald-200">
            <p className="text-sm font-semibold text-emerald-800">{loyaltyLine.line}</p>
            {loyaltyLine.rewardReady && <p className="text-[11px] text-emerald-700">Ya tienes tu recompensa: pídela en caja.</p>}
          </div>
        )}
        <button onClick={doPrint} className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
          Imprimir
        </button>
        {printMsg && (
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-center text-[11px] text-amber-800 ring-1 ring-amber-200">{printMsg}</p>
        )}
        {/* Capa 2 (V96) — factura on-demand de la venta: se auto-oculta sin el
            permiso cfdi_sales:emit; el backend re-valida módulo + emisor listo.
            🅐 pieza 1 — oculto en pre-cuenta (sin pago no hay factura; el guard
            del servidor además lo rechaza). */}
        {!isPreCuenta && !isPromo && (
          <div className="mt-2">
            <FacturarCfdiButton orderId={order.id} />
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// §32/A0 — PENDIENTES DE ENTREGA (mostrador). Lista de pedidos para recoger ya cobrados y
// aún no entregados. Acciones: mandar a cocina (fire), entregar, reimprimir. El inventario
// baja al marcar listo (cocina) / entregar — no aquí. Polling ligero para reflejar cocina.
// ===========================================================================
const FULFILL_LABEL: Record<string, { txt: string; cls: string }> = {
  pending_prepare: { txt: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
  in_kitchen: { txt: 'En cocina', cls: 'bg-emerald-100 text-emerald-700' },
  ready: { txt: 'Listo', cls: 'bg-indigo-100 text-indigo-700' },
};
// Etiqueta legible del estado de entrega (para el ticket). Función = hoisted, la usa TicketView.
function fulfillLabel(s?: string | null): string {
  return s === 'in_kitchen' ? 'En preparación'
    : s === 'ready' ? 'Listo para recoger'
    : s === 'pending_prepare' ? 'Pendiente de preparar'
    : s === 'delivered' ? 'Entregado' : '';
}
function PendingDeliveryView({ onOpenTicket, onClose, sourceModule = 'gastro' }:
  { onOpenTicket: (o: Order) => void; onClose?: () => void; sourceModule?: string }) {
  // V196 — la MISMA pantalla sirve la bandeja de "Pedidos en linea" de boutique/gym
  // (ciclo Pagado -> Listo para recoger -> Entregado, sin cocina). isStore = boutique/gym.
  const isStore = sourceModule !== 'gastro';
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [cobro, setCobro] = useState<Order | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, Order | 'loading'>>({}); // detalle por orden
  // Notificacion: sonido + contador al llegar un pedido nuevo (boutique/gym no tiene cocina
  // que suene). Requiere gesto del usuario (restriccion del navegador).
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef<AudioContext | null>(null);
  const prevCount = useRef<number | null>(null);

  const beep = () => {
    try {
      const ac = audioRef.current; if (!ac) return;
      const o = ac.createOscillator(); const g = ac.createGain();
      o.frequency.value = 880; o.connect(g); g.connect(ac.destination);
      g.gain.setValueAtTime(0.001, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ac.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.25);
      o.start(); o.stop(ac.currentTime + 0.26);
    } catch { /* noop */ }
  };
  const enableSound = () => {
    try { audioRef.current = audioRef.current || new (window.AudioContext || (window as any).webkitAudioContext)(); setSoundOn(true); beep(); }
    catch { /* noop */ }
  };

  const load = async () => {
    try {
      const data = (await api(`/pos/orders/pending-delivery?sourceModule=${encodeURIComponent(sourceModule)}`)) as any[];
      // Suena si LLEGARON pedidos nuevos (el conteo subio respecto al poll anterior).
      if (prevCount.current != null && data.length > prevCount.current && soundOn) beep();
      prevCount.current = data.length;
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    prevCount.current = null;
    load();
    const poll = window.setInterval(load, 7000);
    return () => window.clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceModule, soundOn]);

  const toggleDetail = async (id: string) => {
    if (expanded[id]) { setExpanded((e) => { const c = { ...e }; delete c[id]; return c; }); return; }
    setExpanded((e) => ({ ...e, [id]: 'loading' }));
    try { const o = (await api(`/pos/orders/${id}`)) as Order; setExpanded((e) => ({ ...e, [id]: o })); }
    catch { setExpanded((e) => { const c = { ...e }; delete c[id]; return c; }); }
  };

  const act = async (id: string, path: string) => {
    setBusyId(id); setErr(null);
    try { await api(`/pos/orders/${id}/${path}`, { method: 'POST' }); await load(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo completar la acción'); }
    finally { setBusyId(null); }
  };
  const openCobro = async (id: string) => {
    setBusyId(id); setErr(null);
    try { setCobro((await api(`/pos/orders/${id}`)) as Order); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo cargar la orden'); }
    finally { setBusyId(null); }
  };
  const reprint = async (id: string) => {
    // Antes de cobrar: comanda (sin QR — la orden abierta no tiene CFDI). Después del
    // cobro: ticket de venta (el ticket ya pinta el QR de autofactura para órdenes pagadas).
    try { onOpenTicket((await api(`/pos/orders/${id}`)) as Order); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo cargar el ticket'); }
  };

  return (
    <div>
      {onClose && (
        <button onClick={onClose} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" /> Volver a mesas
        </button>
      )}
      <div className="flex items-start justify-between gap-3">
        <Header title={isStore ? 'Pedidos en línea' : 'Pendientes de entrega'}
          subtitle={isStore ? 'Pedidos en línea para recoger en sucursal.' : 'Pedidos para recoger cobrados y aún no entregados.'} />
        <div className="flex items-center gap-2 pt-1">
          {rows.length > 0 && <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700">{rows.length} pendiente(s)</span>}
          {!soundOn
            ? <button onClick={enableSound} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">🔔 Activar sonido</button>
            : <span className="text-xs font-medium text-emerald-600">🔔 Sonido activo</span>}
        </div>
      </div>
      {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-xs">{err}</p>}
      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
          Sin pedidos pendientes de entrega.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => {
            const st = FULFILL_LABEL[r.fulfillmentStatus] ?? { txt: r.fulfillmentStatus, cls: 'bg-slate-100 text-slate-600' };
            const notFired = r.firedCount < r.itemCount;
            return (
              <div key={r.orderId} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-2xl font-bold tracking-widest text-slate-900">{r.deliveryCode ?? '—'}</p>
                    {r.label && <p className="text-xs text-slate-500">Ref: <span className="font-medium text-slate-600">{r.label}</span></p>}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${st.cls}`}>{st.txt}</span>
                </div>
                <p className="mt-2 text-[11px] text-slate-400">
                  {r.readyCount}/{r.itemCount} listos · {mxn(r.total)}
                  {/* #5 — fuente única del pago: SIN COBRAR / PARCIAL / PAGADO EN LÍNEA / PAGADO. */}
                  {!r.paid && !(r.paidAmount > 0) && <span className="ml-1 font-semibold text-rose-600">· SIN COBRAR</span>}
                  {/* PINK #2 — cobro parcial: saldo pendiente visible en Pendientes. */}
                  {!r.paid && r.paidAmount > 0 && (
                    <span className="ml-1 font-semibold text-amber-600">· PARCIAL — falta {mxn((Number(r.total) || 0) - r.paidAmount)}</span>
                  )}
                  {r.paid && r.paidOnline && <span className="ml-1 font-semibold text-emerald-600">· PAGADO EN LÍNEA</span>}
                  {r.paid && !r.paidOnline && <span className="ml-1 font-semibold text-emerald-600">· PAGADO</span>}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button disabled={busyId === r.orderId} onClick={() => toggleDetail(r.orderId)}
                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    {expanded[r.orderId] ? 'Ocultar' : 'Ver detalle'}
                  </button>
                  {isStore ? (
                    <>
                      {/* Ciclo boutique/gym: Pagado -> Listo para recoger -> Entregado (sin cocina). */}
                      {r.fulfillmentStatus !== 'ready' && (
                        <button disabled={busyId === r.orderId} onClick={() => act(r.orderId, 'ready')}
                          className="rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50">
                          Listo para recoger
                        </button>
                      )}
                      <button disabled={busyId === r.orderId || !r.paid || r.fulfillmentStatus !== 'ready'} onClick={() => act(r.orderId, 'deliver')}
                        title={!r.paid ? 'Falta el pago' : r.fulfillmentStatus !== 'ready' ? 'Marca Listo primero' : 'Entregar'}
                        className="rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        Entregar
                      </button>
                    </>
                  ) : (
                    <>
                      {notFired && (
                        <button disabled={busyId === r.orderId} onClick={() => act(r.orderId, 'fire')}
                          className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                          Mandar a cocina
                        </button>
                      )}
                      {!r.paid && (
                        <button disabled={busyId === r.orderId} onClick={() => openCobro(r.orderId)}
                          className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                          Cobrar
                        </button>
                      )}
                      <button disabled={busyId === r.orderId || !r.paid} onClick={() => act(r.orderId, 'deliver')}
                        title={r.paid ? 'Entregar (baja inventario)' : 'Cobra primero'}
                        className="rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        Entregar
                      </button>
                    </>
                  )}
                  <button disabled={busyId === r.orderId} onClick={() => reprint(r.orderId)}
                    className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    Reimprimir
                  </button>
                </div>
                {expanded[r.orderId] && (
                  <div className="mt-3 border-t border-slate-100 pt-3 text-xs">
                    {expanded[r.orderId] === 'loading' ? <p className="text-slate-400">Cargando…</p> : (() => {
                      const o = expanded[r.orderId] as any;
                      return (
                        <>
                          <ul className="space-y-1">
                            {(o.items ?? []).map((it: any) => (
                              <li key={it.id} className="text-slate-700">{it.qty}× {it.description}
                                {Array.isArray(it.modifiers) && it.modifiers.length > 0 && <span className="text-slate-400"> ({it.modifiers.map((m: any) => m.name).join(', ')})</span>}
                                {it.notes && <span className="italic text-slate-400"> — “{it.notes}”</span>}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-slate-400">Cliente: {o.customerName ?? '—'} · {o.customerPhone ?? '—'}
                            {o.customerPhone && <a href={`https://wa.me/52${String(o.customerPhone).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="ml-2 font-medium text-emerald-600">WhatsApp</a>}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* 🔴 El MISMO modal de cobro del POS, para la orden de la tarjeta. Al pagar:
          recarga la lista (paid=true → "Entregar" se habilita) y abre el ticket de venta. */}
      {cobro && (
        <CobroModal order={cobro} sourceModule="gastro"
          onClose={() => setCobro(null)}
          onPaid={(paid) => { setCobro(null); load(); onOpenTicket(paid); }}
          onPartial={() => { setCobro(null); load(); }} />
      )}
    </div>
  );
}

/**
 * §32/A0 (Hallazgo 4) — "Pendientes de entrega" como DESTINO propio de navegación. Para el
 * mostrador y la cafetería escolar el operador nunca está en "Mesas"; esta pantalla se monta
 * directo desde el menú (Dashboard), sin el "Volver a mesas". Reimprimir abre el ticket aquí.
 */
export function PendingDeliveryScreen({ me, sourceModule = 'gastro' }: { me?: any; sourceModule?: string }) {
  const brandName = me?.tenant?.branding?.brandName || me?.tenant?.name || 'FluidSol';
  const [ticket, setTicket] = useState<Order | null>(null);
  if (ticket) {
    return <TicketView order={ticket} brandName={brandName} backLabel="Volver a pendientes" onClose={() => setTicket(null)} />;
  }
  return <PendingDeliveryView onOpenTicket={setTicket} sourceModule={sourceModule} />;
}

// ===========================================================================
// §17 Part A fix — COMPROBANTE DE CANCELACIÓN. Una comanda cancelada NO es una venta:
// no se cobró nada y NUNCA se puede facturar (el backend rechaza igual). Esta pantalla
// deja constancia imprimible de la cancelación (folio, hora, quién) — sin "Total
// cobrado", sin QR de factura, sin botón "Facturar (CFDI)".
// ===========================================================================
function CancelledReceiptView({ order, brandName, tableName, onClose, backLabel = 'Volver a mesas' }:
  { order: Order; brandName: string; tableName?: string | null; onClose: () => void; backLabel?: string }) {
  const { user } = useAuth();
  const whoCancelled = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email : '—';
  const when = order.closedAt ? new Date(order.closedAt) : new Date();
  const whenLabel = when.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  const folio = order.id.slice(0, 8).toUpperCase();
  const label = tableName ? `Mesa: ${tableName}` : (order.customerLabel || 'Venta rápida');
  const [printMsg, setPrintMsg] = useState<string | null>(null);

  const doPrint = async () => {
    setPrintMsg(null);
    const items = groupOrderItems(order.items).map((g) => ({ qty: g.qty, description: g.rep.description }));
    const cd = { brandName, folio, cancelledBy: whoCancelled, reason: label, items };
    const branchId = (user as any)?.branch?.activeBranchId ?? null;
    const settings = await fetchTicketSettings(branchId, true).catch(() => null);
    const branding = settings ? await resolveBranding(settings) : null;
    const res = await printTicket({
      branchId, kind: 'cancel',
      escpos: (w) => cancelTicket({ ...cd, branding: branding?.escFor(w) ?? null, cols: w === 58 ? 32 : 48 }),
      html: (w) => cancelHtml({ ...cd, branding: branding?.htmlFor(w) ?? null }, w),
    });
    if (res.usedFallback || res.method === 'browser') {
      setPrintMsg(res.error ? `⚠ ${res.error}` : 'Se abrió la vista de impresión del navegador.');
    }
  };

  return (
    <div>
      <button onClick={onClose} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> {backLabel}
      </button>
      <div className="mx-auto max-w-sm rounded-2xl border border-rose-200 bg-white p-6 shadow-sm">
        <div className="mb-4 text-center">
          <p className="font-serif text-xl tracking-tight" style={{ color: 'var(--brand-primary, #0f172a)' }}>{brandName}</p>
          <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rose-700">
            Comanda cancelada
          </p>
        </div>
        <div className="mb-3 space-y-1 border-y border-slate-100 py-3 text-sm">
          <Row label="Folio" value={folio} muted />
          <Row label="Origen" value={label} muted />
          <Row label="Cancelada" value={whenLabel} muted />
          <Row label="Canceló" value={whoCancelled} muted />
        </div>
        {/* Detalle de lo que traía la comanda (referencia; NO es un cobro). */}
        <ul className="mb-3 divide-y divide-slate-100">
          {groupOrderItems(order.items).map((g) => (
            <li key={g.rep.id} className="py-1.5 text-sm">
              <div className="flex items-center justify-between text-slate-500">
                <span><span className="tabular-nums text-slate-400">{g.qty}×</span> {g.rep.description}</span>
                <span className="tabular-nums line-through">{mxn(g.lineTotal)}</span>
              </div>
              <ItemMods item={g.rep} />
            </li>
          ))}
        </ul>
        <p className="text-center text-[11px] text-slate-400">
          Esta comanda se canceló. No se realizó ningún cobro y no puede facturarse.
        </p>
        <button onClick={doPrint} className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
          Imprimir comprobante
        </button>
        {printMsg && (
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-center text-[11px] text-amber-800 ring-1 ring-amber-200">{printMsg}</p>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Nueva mesa
// ===========================================================================
function NewTableModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: '', zone: '', capacity: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: any) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      await api('/pos/tables', { method: 'POST', body: {
        name: f.name.trim(), zone: f.zone.trim() || undefined,
        capacity: f.capacity ? Number(f.capacity) : undefined,
      } });
      onSaved();
    } catch (e2: any) { setErr(e2?.body?.message || e2?.message || 'Error'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-sm space-y-3 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">Nueva mesa</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <input className="input" required placeholder="Nombre (ej. Mesa 5)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <input className="input" placeholder="Zona (opcional)" value={f.zone} onChange={(e) => setF({ ...f, zone: e.target.value })} />
          <input className="input" type="number" min="1" placeholder="Capacidad" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} />
        </div>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} disabled={busy}>{busy ? 'Creando…' : 'Crear'}</button>
        </div>
      </form>
    </div>
  );
}
